import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { collection, query, where, getDocs, doc, updateDoc, getDoc, onSnapshot, orderBy } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { startGPS, stopGPS, clearGPS, triggerHeartbeat } from "../lib/gps";
import { planTimeForStop, computeStopEstimates, formatDelayLabel } from "../lib/stopSchedule";
import { buildCumulativeLengths, projectToPolyline, toLatLngPath, haversine } from "../lib/routeProgress";
import { computeGuide, kakaoMapDirectionsUrl, stopLatLng, splitGuidePath, guideView, nextNaviGuide, travelHeading, navCenter, turnGlyph, turnIconKind, rotateDragDelta, trafficLabel, speedKmh, formatDistance, onPathWithin, rerouteDecision, pathToTarget, latestArrivedIndex, pinchLevel } from "../lib/navGuide";
// 회전 픽토그램(2026-08-06) — 큰 표시는 글리프 문자 대신 직접 그린 SVG(폰트에 없으면 두부가 된다).
import { NavTurnIcon } from "../components/NavTurnIcon";
import { Map, Polyline, CustomOverlayMap } from "react-kakao-maps-sdk";
import { useAnimatedPositions } from "../lib/useAnimatedPositions";
import { buildRunId, recordEtaDiagnostic, throttleGate } from "../lib/etaDiag";
import { ensureGeolocationPermission } from "../lib/usePermissions";
import { useOnlineRecover } from "../lib/useOnlineRecover";
import { kstDepartMs, preDepartureBlocked } from "../lib/arrivalGate";
import { createBoardingToken, getBoardingUrl, validateAndBoardByDriver, boardByNfc, registerNfcCard } from "../lib/boarding";
import { normalizeNfcUid, nfcBytesToHex, formatNfcUid, createTagCooldown } from "../lib/nfc";
import { detectNfcMode, startCardScan, nativeNfcStatus } from "../lib/passengerNfc";
import QRCode from "qrcode";
import jsQR from "jsqr";
import { BusLinkLogo, Pill, StatusDot, Icon } from "../components/ui";
import InstallPrompt from "../components/InstallPrompt";
import { applyAppManifest } from "../lib/pwaManifest";
import PermissionGate from "../components/PermissionGate";
import { isNativeApp } from "../lib/nativeApp";
import { resolveByHostname } from "../lib/companyResolver";
import { useExitConfirm } from "../lib/useExitConfirm";

// 2026-06-01 — stopArrivals 기록 위임 채널(CF `recordStopArrival`).
// 클라이언트 직접 updateDoc 이 silent fail(rules 매핑·drivers.uid 누락 등) 하던 결함
// 차단 — Admin SDK 가 rules 우회. region 명시(PartnerApp 패턴 일관).
const functions = getFunctions(undefined, "us-central1");

// 빌드 식별자 — 진단 JSON 의 appVersion 으로 회수돼 "어느 번들이 실제 폰에서
// 돌았는지" 확정(설치형 PWA 가 옛 캐시를 돌리는 경우 vs 코드 결함 구분). 배포마다 갱신.
const DRIVER_BUILD = "2026-06-09-livegps";

// 리디자인 2단계(2026-05-16): 라이트 테마 리스킨.
// ── 로직 100% 불변: state/effect·init(driver/dispatch/stops 로드)·loadDispatch
//    ·loadStops·Wake Lock·Notification·handleStart/handleStop/refreshToken
//    ·startGPS/stopGPS/clearGPS·createBoardingToken/getBoardingUrl·QRCode.toDataURL
//    ·setInterval 5분 토큰 갱신·signOut·companyId(propCompanyId||"dy001")
//    ·activeTab("운행"|"탑승 QR")·currentStopIdx·App.js 분기 전부 그대로.
//    마크업/스타일(S 객체)만 다크 하드코딩 → tokens.css 변수·components/ui 로 교체.
// ── 목업 design/src/screens-mobile.jsx DriverApp 의 "시각 언어"만 차용:
//    헤더→인사→#0066FF→#003DCC 그라데이션 히어로 배차카드(다음정류장 진행바)
//    →MiniStat 3→QR/탭 버튼→정류장 리스트→하단 검정 운행종료.
//    목업의 가짜 수치(속도 42·GPS ±4m·탑승 38/45·가짜 시간·회차)는 도입 금지 —
//    실제 dispatch/stops/currentStopIdx 데이터만 리스킨.
export default function DriverApp({ companyId: propCompanyId }) {
  // 뒤로가기 종료 확인(마운트 시 1회 발판 push, 인증/로딩 분기와 무관).
  useExitConfirm();
  const [driver, setDriver] = useState(null);
  // 다중 배차 지원: 같은 기사·날짜에 dispatch 여러 건 가능 → 칩으로 선택.
  // 기존 `dispatch` 단일 참조는 derived 값으로 호환(아래 derived dispatch).
  const [dispatches, setDispatches] = useState([]);
  const [activeDispatchId, setActiveDispatchId] = useState(null);
  const dispatch = dispatches.find(d => d.id === activeDispatchId) || null;
  // 배차 선택 모달 (EmployeeApp routePicker 패턴) — 가로 칩을 모달로 교체.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dispatchQuery, setDispatchQuery] = useState("");
  const filteredDispatches = dispatches.filter(d => {
    const q = dispatchQuery.trim().toLowerCase();
    if (!q) return true;
    return (d.routeName || "").toLowerCase().includes(q)
      || (d.vehicleNo || "").toLowerCase().includes(q)
      || (d.departTime || "").includes(q)
      || (d.partnerName || "").toLowerCase().includes(q);
  });
  const [stops, setStops] = useState([]);
  const [routePath, setRoutePath] = useState([]); // 노선 사전경로 — GPS 복구 백필·estimates 구간전파용
  // 노선 데이터(stops+routePath) 로드 완료 플래그(2026-06-02). startGPS 는 호출 시점의
  // stops/routePath 를 1회 캡처 → 로드 전 "운행 시작"을 누르면 빈 routePath 가 캡처돼
  // 도착 진행률 백필이 세션 내내 비활성화(고속 주행 시 100m 직접 감지만으론 정류장
  // 통과를 놓침 → 도착시간 0건). 로드 완료 전 운행 시작 버튼을 막아 경합 차단.
  const [routeDataReady, setRouteDataReady] = useState(false);
  const [currentStopIdx, setCurrentStopIdx] = useState(-1);
  // [DIAG-ETA 제거예정] 진단 오버레이 토글 (운행 중일 때만 노출, 기본 닫힘)
  // 사용자가 폰 실측 시 정류장별 ETA·source·delay를 직접 캡쳐하기 위한 임시 카드.
  // 다음 커밋에 grep "[DIAG-ETA" 1줄로 통째 제거.
  const [diagOpen, setDiagOpen] = useState(false);
  // [/DIAG-ETA]
  const [boardingToken, setBoardingToken] = useState(null);   // 현재 탑승 토큰
  const [qrUrl, setQrUrl] = useState(null);        // 탑승 링크 URL
  const [qrDataUrl, setQrDataUrl] = useState(null); // canvas → base64 이미지
  const [activeTab, setActiveTab] = useState("운행");          // "운행" | "QR"
  // 길안내 = 지도 중심 모드(2026-08-05 way). 인사·배차 히어로·상태 스트립을 접고
  // 지도를 화면 높이만큼 키운다 — 운전 중 필요한 건 지도와 다음 회전뿐이다.
  const navFocus = activeTab === "길안내";
  const tokenTimerRef = useRef(null);
  // 협력사 boardingMode (2026-05-27, 역방향 QR) — 'driver-qr'(기본·null도 동치) | 'passenger-qr'
  // dispatch.routeId → routes/{id}.partnerCode → partnerCodes/{code}.boardingMode 체인.
  // 미설정·null·조회 실패 = 'driver-qr' (기본·회귀 0).
  const [boardingMode, setBoardingMode] = useState("driver-qr");
  const [nextStopDist, setNextStopDist] = useState(null);
  const [driving, setDriving] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Phase 1.1 (2026-05-28): App.js propCompanyId 우선, 없으면 hostname 매핑, 최종 dy001.
  // /driver 직접 진입(Auth users 문서 미존재) 시에도 hostname 으로 자기 회사 자동 결정.
  const [companyId, setCompanyId] = useState(
    propCompanyId || resolveByHostname(window.location.hostname) || "dy001"
  );
  const [gpsStatus, setGpsStatus] = useState("");   // GPS 신호 안내 ("" | "확보중" | "권한")
  // 배정 차량의 위치 신호 방식(RQ/20260708 #2) — "device" 면 GPS 단말로 서버 자동 추적,
  // 앱은 startGPS 호출 안 함. companies/{cid}/vehicles/{vehicleId}.gpsSource 1회 getDoc.
  const [deviceGps, setDeviceGps] = useState(false);
  // 도착 감지 반경(m) — 회사 설정값(companies/{cid}.stopArriveRadiusM). 미설정=100m(기존 동작).
  // startGPS 가 이 값으로 정류장 도착을 판정. 관리자가 운행 이력 화면에서 조정.
  const arriveRadiusRef = useRef(100);
  const wakeLockRef = useRef(null);
  const currentStopRowRef = useRef(null);
  // ETA 자동 진단 로깅(2026-05-29) — 운행 1회 단위 runId. 운행 시작 시 산출, 종료 시 null.
  // 30초 interval + stopArrivals 변경 시 즉시 1건 기록 → etaDiagnostics/{date}/runs/{runId}/points.
  const [runId, setRunId] = useState(null);
  // 마지막 차량 GPS 좌표(diagnostic 기록용) — gps/{companyId}_{vehicleId} 본인 발신값 1회 getDoc.
  const lastVehiclePosRef = useRef(null);
  const lastVehicleSpeedRef = useRef(null);
  // 2026-06-01 — stopArrivals 기록 결과 로그(진단 첨부용, 최근 20건만 유지).
  // 각 entry = { stopId, ok, alreadyExists?, error?, code?, viaBackfill, ts }
  // 다음 진단 JSON(recordEtaDiagnostic) 에 stopArrivalsLog 로 첨부 → 어느 정류장이
  // 어떻게 기록됐는지(권한 거부·not-found·alreadyExists 등) 추적 가능.
  const stopArrivalsLogRef = useRef([]);
  // 2026-06-08 — busProgress 기반 통과 정류장 기록 중복 방지(이번 운행 세션 단위).
  // gps.js detectStops(onStopReached) 가 일부 노선에서 발화 안 하는 문제 우회:
  // DriverApp 이 신뢰 가능한 busProgress 로 직접 통과 판정 → recordStopArrival(실시각).
  const gpsRecordedRef = useRef(new Set());
  // ref 로 최신 노선데이터 노출(2026-06-02) — startGPS 는 호출 시점의 stops/routePath 를
  // 1회 캡처하는데, 비동기 로드 전이면 빈값에 갇혀 도착 백필이 세션 내내 비활성(2일 연속
  // 도착 0건의 근인). gps.js 가 매 감지 시 ref.current(최신 state)를 읽도록 getter 주입 →
  // 로드 타이밍·버튼 게이트 경합과 무관하게 백필 작동. 렌더마다 최신값 동기화.
  const stopsRef = useRef([]);
  // 서버 도착 기록(stopId→ms) 최신값 — startGPS 콜백(canRecordStop)이 클로저로 옛 값을 잡지 않게(2026-10-30).
  const stopArrivalsRef = useRef({});
  const originArrivedNow = () => {
    const o = stopsRef.current && stopsRef.current[0];
    return !!(o && (stopArrivalsRef.current[o.id] != null || gpsRecordedRef.current.has(o.id)));
  };
  const routePathRef = useRef([]);
  stopsRef.current = stops;
  routePathRef.current = Array.isArray(routePath) ? routePath : [];
  // 오프라인→온라인 복구 시 onSnapshot 재구독 + Firestore reconnect 강제(2026-05-28).
  // 통신 안 좋다가 좋아져도 기사앱이 자동 활성화 안 되던 결함 차단 — DriverApp은 GPS 발신측
  // 이라 useWakeTick 적용 안 했었으나, 장시간 오프라인 후 stale 리스너 가능성 확인되어
  // useOnlineRecover로 보강. forceFirestoreReconnect=true 로 disableNetwork→enableNetwork
  // 1회 강제(reconnect 지연 우회).
  const recoverTick = useOnlineRecover({ forceFirestoreReconnect: true });

  // 현재 정류장 변경 시 리스트에서 자동 스크롤(중앙 정렬) — 운행 시작 전/리스트 없음/이미 보임 케이스는 skip
  useEffect(() => {
    if (currentStopIdx < 0) return;
    const el = currentStopRowRef.current;
    if (!el || typeof el.scrollIntoView !== "function") return;
    // 약간의 지연 — 카드 배경 전환 직후 위치 안정 후 스크롤
    const t = setTimeout(() => {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* 일부 구형 브라우저 무해 */ }
    }, 80);
    return () => clearTimeout(t);
  }, [currentStopIdx]);

  // 알림 권한 요청
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // 설치형 앱(PWA) 조건용 SW 등록 1회 — 브라우저가 URL+scope 로 dedupe.
  // DriverApp 은 FCM 미사용이나 설치 가능 조건 충족 위해 등록만 추가. 실패 무해 처리.
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .catch(() => {});
    }
  }, []);

  // 앱별 PWA 설치 아이콘(기사앱) — DriverApp 은 / 에서 Auth 역할 분기로 마운트되므로
  // 정적 path-scope 불가 → 마운트 시점에 manifest/apple-touch/제목 교체. 1회.
  useEffect(() => {
    applyAppManifest({
      manifestHref: "/manifest-driver.json",
      appleTouchHref: "/icons/driver-1024.png",
      title: "BusLink 기사",
    });
  }, []);

  // 로그인 직후 init 1회 자동 재시도 가드(transient 흡수). "다시 시도" 버튼은 별도(가드 무관).
  const autoRetriedRef = useRef(false);
  // init() 을 재호출 가능한 함수로 추출(2026-06-22) — 로그인 직후 일시적 Firestore/토큰 경합으로
  // getDocs 가 가끔 throw 하면 종료성 에러화면(로그아웃만)에 갇히던 결함 복구.
  //   - transient(catch) 1회 한정 ~1.5초 후 자동 재시도(autoRetriedRef 가드·무한루프 차단).
  //   - 그래도 실패하면 에러 화면의 "다시 시도" 버튼으로 재실행(재로그인 불필요).
  //   - "기사 정보를 찾을 수 없습니다"(데이터 부재)는 재시도 무의미 → 자동 재시도 대상에서 제외.
  const init = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) {
      // currentUser 일시 null 엣지(토큰 경합) — 종료성 에러 대신 재시도 가능한 안내.
      console.warn("[BusLink] init: auth.currentUser 부재(일시적 경합 가능)");
      setError("로그인 정보를 확인하지 못했습니다.\n잠시 후 다시 시도해주세요.");
      setLoading(false);
      return;
    }
    const cid = propCompanyId || resolveByHostname(window.location.hostname) || "dy001";
    setCompanyId(cid);
    setError("");
    setLoading(true);
    try {
      let snap = await getDocs(query(
        collection(db, "companies", cid, "drivers"),
        where("uid", "==", u.uid)
      ));
      if (snap.empty && u.email?.endsWith("@buslink.com")) {
        const empNo = u.email.replace("@buslink.com", "");
        snap = await getDocs(query(
          collection(db, "companies", cid, "drivers"),
          where("empNo", "==", empNo)
        ));
        if (!snap.empty) {
          await updateDoc(doc(db, "companies", cid, "drivers", snap.docs[0].id), { uid: u.uid });
        }
      }
      if (!snap.empty) {
        const d = { id: snap.docs[0].id, ...snap.docs[0].data() };
        setDriver(d);
        await loadDispatch(d.id, cid);
        setError("");
      } else {
        // 데이터 부재 — 재시도 무의미(자동 재시도 제외).
        setError("기사 정보를 찾을 수 없습니다.\n관리자에게 문의하세요.");
      }
    } catch (e) {
      // transient(Firestore/토큰 경합) 가능 — 향후 진단 위해 실제 에러 로그.
      console.warn("[BusLink] init 실패:", e?.code || "", e?.message || e);
      if (!autoRetriedRef.current) {
        // 첫 실패 → ~1.5초 후 1회 자동 재시도(가드로 무한루프 차단).
        autoRetriedRef.current = true;
        setTimeout(() => { init(); }, 1500);
        return; // loading 유지 — 재시도 결과로 갱신
      }
      setError("데이터 로드 중 오류가 발생했습니다.\n네트워크 확인 후 다시 시도해주세요.");
    }
    setLoading(false);
    // loadDispatch 는 컴포넌트 내 안정적 함수(상태 의존 없음·인자 기반) — deps 제외.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propCompanyId]);

  useEffect(() => {
    // onAuthStateChanged 구독으로 보강 — auth.currentUser 일시 null 엣지(로그인 직후
    // 토큰 경합) 방어. user 확정 시 init 1회 호출. App.js 가 이미 인증 게이트라 정상
    // 흐름엔 즉시 발화. unsubscribe 로 정리.
    const unsub = auth.onAuthStateChanged((u) => {
      if (u) { autoRetriedRef.current = false; init(); }
    });
    return () => unsub();
  }, [init]);

  const loadDispatch = async (driverId, cid) => {
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
    const snap = await getDocs(query(
      collection(db, "companies", cid, "dispatches", today, "list"),
      where("driverId", "==", driverId)
    ));
    if (snap.empty) { setDispatches([]); setActiveDispatchId(null); return; }
    const list = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.departTime || "").localeCompare(b.departTime || ""));  // 출발시간 오름차순
    setDispatches(list);
    // 활성 선택: localStorage 우선(같은 날 재진입 시 복원) > 첫 항목
    let pick = null;
    try {
      const saved = localStorage.getItem(`buslink_driver_active_dispatch_${today}`);
      if (saved && list.some(d => d.id === saved)) pick = saved;
    } catch {}
    pick = pick || list[0].id;
    setActiveDispatchId(pick);
    // 정류장 로드는 activeDispatchId 변경 useEffect 가 책임(중복 호출 회피)
  };

  const loadStops = async (routeId, cid) => {
    setRouteDataReady(false); // 로드 시작 — 완료까지 운행 시작 게이트
    try {
      const snap = await getDocs(query(
        collection(db, "companies", cid, "routes", routeId, "stops"),
        orderBy("order", "asc")
      ));
      setStops(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.warn("[BusLink] 정류장 로드 실패:", e.message);
    }
    // 노선 사전경로(routePath) 로드 — GPS 복구 시 정류장 진행률 백필·estimates
    // 구간전파에 사용. 빈배열/없음이면 직선 폴백(하위호환 — 회귀 0).
    try {
      const rd = await getDoc(doc(db, "companies", cid, "routes", routeId));
      const rp = rd.exists() ? rd.data().routePath : null;
      setRoutePath(Array.isArray(rp) ? rp : []);
    } catch (e) {
      console.warn("[BusLink] 노선 경로 로드 실패:", e.message);
      setRoutePath([]);
    }
    // stops + routePath fetch 완료 → 운행 시작 허용(startGPS 가 실 데이터 캡처 보장).
    setRouteDataReady(true);
  };

  // 운행 중 "GPS 신호 확보 중…" 안내 자동 해제 — 실제 측위가 들어오면 표시 끔.
  // (startGPS 파이프라인을 건드리지 않고 동일 위치권한으로 1회 확인 — 권한 거부는 유지)
  useEffect(() => {
    if (!driving || gpsStatus !== "확보중") return;
    let cancelled = false;
    const w = navigator.geolocation.watchPosition(
      () => { if (!cancelled) setGpsStatus(""); },
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 }
    );
    return () => { cancelled = true; navigator.geolocation.clearWatch(w); };
  }, [driving, gpsStatus]);

  // 활성 배차 변경 시 stops 재로드 + 선택 영속화
  useEffect(() => {
    if (!dispatch?.routeId || !companyId) { setStops([]); setRouteDataReady(false); return; }
    loadStops(dispatch.routeId, companyId);
    try {
      const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
      localStorage.setItem(`buslink_driver_active_dispatch_${today}`, dispatch.id);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDispatchId, dispatch?.routeId, companyId]);

  // boardingMode 조회 (2026-05-27 역방향 QR) — 우선순위: routes.boardingMode > partnerCodes.boardingMode > 'driver-qr'.
  // 노선 단위 override가 우선 — 혼승 노선(여러 협력사 직원 공유) 대응. 명시 미설정 시 협력사 정책 fallback.
  // 'passenger-qr' 시 기사앱이 발행 대신 카메라로 스캔. 미설정·null·실패 = 'driver-qr'(회귀 0).
  useEffect(() => {
    let cancelled = false;
    if (!dispatch?.routeId || !companyId) { setBoardingMode("driver-qr"); return; }
    (async () => {
      try {
        const routeSnap = await getDoc(doc(db, "companies", companyId, "routes", dispatch.routeId));
        if (!routeSnap.exists()) { if (!cancelled) setBoardingMode("driver-qr"); return; }
        const r = routeSnap.data();
        // (1) 노선 단위 override
        if (r.boardingMode === "passenger-qr" || r.boardingMode === "driver-qr") {
          if (!cancelled) setBoardingMode(r.boardingMode);
          return;
        }
        // (2) 협력사 단위 fallback
        const partnerCode = r.partnerCode || null;
        if (!partnerCode) { if (!cancelled) setBoardingMode("driver-qr"); return; }
        const pcSnap = await getDoc(doc(db, "partnerCodes", partnerCode));
        if (cancelled) return;
        const mode = pcSnap.exists() ? (pcSnap.data().boardingMode || "driver-qr") : "driver-qr";
        setBoardingMode(mode === "passenger-qr" ? "passenger-qr" : "driver-qr");
      } catch (e) {
        if (!cancelled) {
          console.warn("[boardingMode 조회 실패]", e.message);
          setBoardingMode("driver-qr"); // 안전한 폴백
        }
      }
    })();
    return () => { cancelled = true; };
  }, [dispatch?.routeId, companyId]);

  // ── 활성 dispatch 문서 실시간 구독 — stopArrivals 표시·멱등 가드용 ──
  // 운행중일 때만 구독(미운행 시엔 정류장 표시 자체가 없으므로 비용 절감).
  // dispatch.stopArrivals = { [stopId]: { actualAt: serverTimestamp, plannedAt, delaySec } }
  const [stopArrivals, setStopArrivals] = useState({});
  useEffect(() => {
    if (!companyId || !dispatch?.id) { stopArrivalsRef.current = {}; setStopArrivals({}); return; }
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
    const ref = doc(db, "companies", companyId, "dispatches", today, "list", dispatch.id);
    return onSnapshot(ref, snap => {
      if (!snap.exists()) { stopArrivalsRef.current = {}; setStopArrivals({}); return; }
      const sa = snap.data().stopArrivals || {};
      const out = {};
      Object.entries(sa).forEach(([sid, v]) => {
        const at = v?.actualAt?.toMillis ? v.actualAt.toMillis() : (typeof v?.actualAt === 'number' ? v.actualAt : null);
        if (at != null) out[sid] = at;
      });
      stopArrivalsRef.current = out;
      setStopArrivals(out);
    }, () => setStopArrivals({}));
    // recoverTick 변화 시 본 effect cleanup→재실행으로 onSnapshot 재구독(통신 복구 후
    // stale 리스너 신선화). companyId/dispatch.id 외 deps라 정상 흐름엔 영향 0.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dispatch?.id, recoverTick]);

  // ── 서버에 기록된 도착으로 현재 정류장 따라잡기 (2026-10-22 way 실주행) ──
  // currentStopIdx 는 휴대폰 감지(onStopReached)로만 올라갔다. 감지가 놓친 정류장은 아래
  // 진행거리 백필이 서버에 도착을 남겨도 화면 값이 그대로라 길안내가 **이미 지난 정류장을
  // 계속 안내**했다(10-22 과천대로 — 서버 도착 10/10 인데 안내가 앞 정류장에 묶임).
  // 판정은 «이번 운행에서 가장 최근 도착»(navGuide.latestArrivedIndex) — 큰 인덱스가 아니다.
  // 🔴 역행 금지(Math.max) · 운행 시작 시각은 runId 끝자리(buildRunId 의 startedAtMs).
  useEffect(() => {
    if (!driving || !runId) return;
    const since = Number(String(runId).split("_").pop());
    const idx = latestArrivedIndex(stops, stopArrivals, Number.isFinite(since) ? since : null);
    if (idx >= 0) setCurrentStopIdx(prev => Math.max(prev, idx));
  }, [stopArrivals, driving, runId, stops]);

  // ── 본인 차량 GPS 실시간 구독 (2026-06-09) ──────────────────────────────
  // 이전엔 computeStopEstimates 에 lastVehiclePosRef(flushDiag 30초 주기)를 넘겨 도착
  // 표시가 ~30초 늦던 문제(사용자 보고) → 자기 gps doc onSnapshot 으로 ~5초(startGPS
  // throttle) 신선화. driving 중에만 구독(비용 절감). recoverTick 으로 통신복구 재구독.
  const [liveVehiclePos, setLiveVehiclePos] = useState(null);
  useEffect(() => {
    if (!driving || !companyId || !driver?.vehicleId) { setLiveVehiclePos(null); return; }
    const ref = doc(db, "gps", `${companyId}_${driver.vehicleId}`);
    return onSnapshot(ref, snap => {
      if (!snap.exists()) return;
      const g = snap.data();
      if (typeof g.lat === "number" && typeof g.lng === "number") {
        setLiveVehiclePos({ lat: g.lat, lng: g.lng, speed: typeof g.speed === "number" ? g.speed : null });
      }
    }, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, driver?.vehicleId, driving, recoverTick]);

  // ── busProgress 기반 통과 정류장 도착 기록 (2026-06-09, 실시간 위치 기반) ──
  // gps.js detectStops(onStopReached)가 일부 노선에서 미발화하는 문제 우회. liveVehiclePos
  // 갱신마다(~5초) busProgress 로 통과 판정 → recordStopArrival(actualAt=serverTimestamp).
  // 실 stopArrivals 가 쌓여 기사앱·승객앱 실시각 표시 + 탑승통계/도착임박푸시 정상화.
  // 세션 중복가드(gpsRecordedRef) + Firestore 기존값 skip + CF 멱등. (이전 flushDiag 30초
  // 기록기 대체 — 실시간이라 도착 기록 지연도 해소.)
  useEffect(() => {
    if (!driving || !companyId || !dispatch?.id || !liveVehiclePos) return;
    const rp = Array.isArray(routePath) && routePath.length >= 2 ? routePath : null;
    if (!rp || !Array.isArray(stops) || stops.length === 0) return;
    let cum, busProg;
    try {
      cum = buildCumulativeLengths(rp);
      const proj = projectToPolyline(liveVehiclePos, rp, cum);
      if (!proj) return;
      busProg = proj.progress;
    } catch { return; }
    const todayRec = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
    for (let i = 0; i < stops.length; i++) {
      const s = stops[i];
      let sp = null;
      try {
        if (typeof s.lat === "number" && typeof s.lng === "number") {
          sp = projectToPolyline({ lat: s.lat, lng: s.lng }, rp, cum)?.progress ?? null;
        }
      } catch { sp = null; }
      if (sp == null || busProg <= sp + 40) continue;              // 아직 미통과
      // 출발 전(차고지→기점 길)엔 백필 기록 안 함 — 마킹도 안 해서 운행 시작 후 다시 판정(2026-10-29)
      if (preDepartureBlocked({ departMs: kstDepartMs(todayRec, dispatch?.departTime), nowMs: Date.now(), isOrigin: i === 0, viaBackfill: true, originArrived: !!(stops[0] && stopArrivals && stopArrivals[stops[0].id] != null) })) continue;
      if (gpsRecordedRef.current.has(s.id)) continue;              // 이번 세션 이미 기록 시도
      if (stopArrivals && stopArrivals[s.id] != null) { gpsRecordedRef.current.add(s.id); continue; } // 이미 있음
      gpsRecordedRef.current.add(s.id);                            // 호출 전 마킹
      const plannedAtRec = planTimeForStop(dispatch?.departTime, s.offsetMin);
      let delaySecRec = null;
      if (plannedAtRec && typeof s.offsetMin === "number") {
        const mm = plannedAtRec.match(/^(\d{2}):(\d{2})$/);
        if (mm) { const pd = new Date(); pd.setHours(+mm[1], +mm[2], 0, 0); delaySecRec = Math.round((Date.now() - pd.getTime()) / 1000); }
      }
      httpsCallable(functions, "recordStopArrival")({
        companyId, date: todayRec, dispatchId: dispatch.id, stopId: s.id,
        plannedAt: plannedAtRec || null, delaySec: delaySecRec, viaBackfill: true,
      }).then(res => {
        stopArrivalsLogRef.current.push({ stopId: s.id, ok: true, alreadyExists: !!(res && res.data && res.data.alreadyExists), via: "liveGps", ts: Date.now() });
        if (stopArrivalsLogRef.current.length > 40) stopArrivalsLogRef.current.shift();
      }).catch(e => {
        gpsRecordedRef.current.delete(s.id);                       // 실패 시 다음 갱신 재시도
        stopArrivalsLogRef.current.push({ stopId: s.id, ok: false, error: e && e.message, code: e && e.code, via: "liveGps", ts: Date.now() });
        if (stopArrivalsLogRef.current.length > 40) stopArrivalsLogRef.current.shift();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveVehiclePos, driving, companyId, dispatch?.id]);

  // 운행 중 "지연 N분"(현재−계획) 표시를 분 단위로 갱신하는 시계 틱(30초, 2026-06-04).
  // GPS 콜백 사이 정지 중에도 정류장 카드의 지연이 흐르도록. 미운행 시 타이머 없음.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!driving) return;
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [driving]);

  // 통신 복구(online 전이) + 운행 중 + watchId 보유 시 즉시 1회 heartbeat sendGPS —
  // 승객앱·관제 마커가 옛 좌표에 멈춰있던 결함 차단. recoverTick 증가가 트리거 신호.
  useEffect(() => {
    if (recoverTick === 0) return; // 첫 마운트 skip
    if (!driving || !watchId) return;
    const ok = triggerHeartbeat(watchId);
    if (ok) console.log("[BusLink] 통신 복구 — heartbeat sendGPS 1회 발송");
  }, [recoverTick, driving, watchId]);

  // Wake Lock 재획득
  useEffect(() => {
    const fn = async () => {
      if (document.visibilityState === "visible" && driving && "wakeLock" in navigator) {
        try { wakeLockRef.current = await navigator.wakeLock.request("screen"); } catch {}
      }
    };
    document.addEventListener("visibilitychange", fn);
    return () => document.removeEventListener("visibilitychange", fn);
  }, [driving]);

  // ── 정류장 estimates(계획·예상시각·지연) ──
  // 기사 화면엔 GPS 가중 무의미(차량 자기 좌표) → vehiclePos 미주입, plan+delay 모드.
  // routePath는 주입 — computeStopEstimates 단조증가 구간전파(chainCandidate)가
  // routePath 구간거리 기반으로 더 정밀해짐(vehiclePos 없으면 gpsCandidate는 미발생,
  // 회귀 표면 없음). todayDispatch.stopArrivals 갱신 → 누적지연 자동 반영.
  // 단조증가 로직이 "도착예정시간이 현재시간 이전" 결함을 해결(이슈 #2).
  const stopEstimates = computeStopEstimates({
    stops,
    departTime: dispatch?.departTime,
    actualArrivals: stopArrivals,
    // 2026-06-09 — 실시간 차량 위치 주입(liveVehiclePos onSnapshot, ~5초). GPS 진척률 통과
    // 안전망(stopSchedule)이 도착 감지 미발화 시에도 버스 실위치로 통과 정류장 판정 →
    // 출발지 기준 균일지연 아티팩트 차단 + 도착 표시 지연(~30초→~5초) 해소(사용자 보고).
    vehiclePos: liveVehiclePos,
    speed: liveVehiclePos?.speed ?? null,
    routePath: Array.isArray(routePath) && routePath.length >= 2 ? routePath : null,
  });
  const estByStopId = Object.fromEntries(stopEstimates.map(e => [e.stopId, e]));

  // ── ETA 자동 진단 로깅(2026-05-29) ─────────────────────────────────────────
  // 운행 중일 때만 활성. 30초 interval + stopArrivals 변경 시 즉시 1건 기록.
  // 차량 좌표는 gps/{companyId}_{vehicleId} 1회 getDoc(본인 발신값 — driverApp이 발신측).
  // routePath 유효 시 busProgress / nextStopProgress 도 같이 산출(부모 진단용).
  const flushDiag = useRef(null);
  flushDiag.current = async (triggerSource) => {
    if (!driving || !runId || !companyId || !dispatch?.id) return;
    // 차량 GPS 1회 조회(본인 발신 doc — 비용 1회 read)
    let vehiclePos = null, vehicleSpeed = null;
    try {
      if (driver?.vehicleId) {
        const gpsSnap = await getDoc(doc(db, "gps", `${companyId}_${driver.vehicleId}`));
        if (gpsSnap.exists()) {
          const g = gpsSnap.data();
          if (typeof g.lat === "number" && typeof g.lng === "number") {
            vehiclePos = { lat: g.lat, lng: g.lng };
            vehicleSpeed = typeof g.speed === "number" ? g.speed : null;
            lastVehiclePosRef.current = vehiclePos;
            lastVehicleSpeedRef.current = vehicleSpeed;
          }
        }
      }
    } catch { /* 무해 — null 인 채로 기록 */ }
    // busProgress / nextStopProgress 산출 (routePath 유효 + vehiclePos 있을 때만)
    let busProgress = null, nextStopProgress = null, allStopProgress = null;
    const validPath = Array.isArray(routePath) && routePath.length >= 2;
    if (validPath) {
      try {
        const cum = buildCumulativeLengths(routePath);
        // 전 정류장 progress(2026-06-08 진단) — 정류장이 routePath 에 정상 투영되는지
        // (값들이 단조증가 분포인지 vs 한 점에 뭉치는지) 확인 → 도착 감지 실패 근인 판별.
        allStopProgress = stops.map(s =>
          (typeof s.lat === "number" && typeof s.lng === "number")
            ? (projectToPolyline({ lat: s.lat, lng: s.lng }, routePath, cum)?.progress ?? null)
            : null
        );
        if (vehiclePos) {
          const proj = projectToPolyline(vehiclePos, routePath, cum);
          if (proj) busProgress = proj.progress;
          // nextIdx = stopEstimates 의 status==='next' 첫 항목 인덱스(stops 배열 기준).
          const nextStopId = (stopEstimates.find(e => e.status === "next") || {}).stopId;
          if (nextStopId) {
            const ns = stops.find(s => s.id === nextStopId);
            if (ns && typeof ns.lat === "number" && typeof ns.lng === "number") {
              const sp = projectToPolyline({ lat: ns.lat, lng: ns.lng }, routePath, cum);
              if (sp) nextStopProgress = sp.progress;
            }
          }
        }
      } catch { /* 무해 */ }
    }
    // (busProgress 통과 정류장 기록은 2026-06-09 실시간 liveVehiclePos useEffect 로 이전 —
    //  flushDiag 30초 주기 대신 ~5초 GPS 갱신마다 기록 → 도착 기록 지연 해소.)

    // nextIdx 산출 (stops 배열 기준 — etaDiag payload용)
    const nextIdx = stops.findIndex(s => {
      const e = estByStopId[s.id];
      return e && e.status === "next";
    });
    // 2026-06-01 — stopArrivals 기록 결과 로그 첨부(최근 20건).
    // 어느 정류장이 ok/alreadyExists/error/code 로 기록됐는지 다음 진단 JSON 에서 추적.
    const stopArrivalsLog = stopArrivalsLogRef.current.slice(-20);
    await recordEtaDiagnostic({
      companyId, runId,
      vehiclePos, vehicleSpeed,
      busProgress, nextStopProgress, allStopProgress,
      estimates: stopEstimates,
      nextIdx,
      T_NOW: Date.now(),
      source: triggerSource || "auto",
      stopArrivalsLog,
      appVersion: DRIVER_BUILD,
    });
  };

  // 30초 interval — 운행 중 + runId 있을 때만.
  useEffect(() => {
    if (!driving || !runId) return;
    const tick = () => {
      if (!throttleGate({ runId, intervalMs: 30_000 })) return;
      flushDiag.current && flushDiag.current("interval");
    };
    // 첫 tick 즉시(throttle 통과 — 마지막 기록 없음)
    tick();
    const t = setInterval(tick, 30_000);
    return () => clearInterval(t);
  }, [driving, runId]);

  // stopArrivals 변경 시 즉시 1건 기록(throttle 우회 — 이벤트 기반 핵심 데이터)
  useEffect(() => {
    if (!driving || !runId) return;
    // 첫 마운트(빈 stopArrivals)는 interval이 처리 — stopArrivals 갱신만 트리거.
    if (!stopArrivals || Object.keys(stopArrivals).length === 0) return;
    throttleGate({ runId, force: true }); // last 시각 갱신(다음 interval 30초 카운트 리셋)
    flushDiag.current && flushDiag.current("stopArrivals");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopArrivals, driving, runId]);

  const sendNotification = (stop, dist) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("🚌 정류장 도착", {
        body: `${stop.name} 도착 (${dist}m)`,
        icon: "/favicon.ico",
      });
    }
  };

  // 배정 차량 gpsSource 로드(1회 getDoc) — device 면 앱 GPS 전송을 건너뛰고 안내 배너.
  // 실패/부재는 mobile 취급(기존 흐름 100% 그대로 — 회귀 0).
  useEffect(() => {
    if (!companyId || !driver?.vehicleId) { setDeviceGps(false); return; }
    let alive = true;
    (async () => {
      try {
        const vSnap = await getDoc(doc(db, "companies", companyId, "vehicles", driver.vehicleId));
        if (!alive) return;
        // rtk(hi-rtk 웹훅·2026-10-15)도 서버가 위치를 쓴다 — device 와 같이 앱 GPS 전송을 건너뛴다(이중 작성 방지).
        setDeviceGps(vSnap.exists() && ["device", "rtk"].includes((vSnap.data() || {}).gpsSource));
      } catch (e) {
        if (alive) setDeviceGps(false);
      }
    })();
    return () => { alive = false; };
  }, [companyId, driver?.vehicleId]);

  // 회사 도착 감지 반경(companies/{cid}.stopArriveRadiusM) 1회 로드 → ref.
  // 미설정/비정상이면 100m 유지(기존 동작). startGPS 가 운행 시작 시 이 값을 캡처.
  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, "companies", companyId)).then(snap => {
      const v = snap.data()?.stopArriveRadiusM;
      if (typeof v === "number" && isFinite(v) && v > 0) arriveRadiusRef.current = v;
    }).catch(() => {});
  }, [companyId]);

  const handleStart = async () => {
    if (!driver.vehicleId) {
      alert("배정된 차량이 없습니다.\n관리자에게 차량 배정을 요청하세요.");
      return;
    }

    // ── GPS 단말(device) 차량: 앱 위치 전송/권한 불필요 — 서버(pollDeviceVehicleGps)가 자동 추적.
    // startGPS 호출을 건너뛰고 운행 상태만 기록. mobile 경로는 아래 그대로(회귀 0).
    if (deviceGps) {
      updateDoc(doc(db, "companies", companyId, "drivers", driver.id), {
        status: "운행중", startedAt: new Date().toISOString(),
      }).catch(e => console.warn("[BusLink] drivers 상태 update 실패(통신):", e.message));
      setGpsStatus("");
      setDriving(true);
      setRunId(buildRunId({ driverId: driver.id, vehicleId: driver.vehicleId, startedAtMs: Date.now() }));
      if (boardingMode !== "passenger-qr") {
        refreshToken(driver, dispatch);
        tokenTimerRef.current = setInterval(() => refreshToken(driver, dispatch), 5 * 60 * 1000);
      }
      return;
    }

    // 위치 권한 사전 점검(작업4 헬퍼 재사용) — 미허용이면 startGPS 전에 권한 요청 흐름.
    // ensureGeolocationPermission: granted|prompt면 OS 팝업 유도, denied면 false 반환.
    setGpsStatus("확보중");
    const ok = await ensureGeolocationPermission();
    if (!ok) {
      setGpsStatus("권한");
      alert("GPS 권한이 필요합니다.\n위치 권한을 허용해주세요.");
      return;
    }
    // ── 2026-05-28 버튼 응답성 수정 ─────────────────────────────────────────
    // 통신 불량 시 `await updateDoc`이 hang하면 setDriving(true) 등 로컬 state 갱신이
    // 영영 실행 안 됨 → 사용자 인식 "버튼이 안 눌림". 해법 = 로컬 state·startGPS는 즉시,
    // Firestore 쓰기·refreshToken은 fire-and-forget(`.catch` 로 graceful).
    // WakeLock도 await 하지 말고 background — 실패해도 운행은 진행.
    if ("wakeLock" in navigator) {
      navigator.wakeLock.request("screen")
        .then(w => { wakeLockRef.current = w; })
        .catch(() => {});
    }
    // Firestore drivers 상태 update — fire-and-forget. 실패해도 운행 흐름 차단 X.
    updateDoc(doc(db, "companies", companyId, "drivers", driver.id), {
      status: "운행중", startedAt: new Date().toISOString(),
    }).catch(e => console.warn("[BusLink] drivers 상태 update 실패(통신):", e.message));
    // 운행 시작 시점의 활성 dispatch·노선 정보를 캡쳐(중간에 dispatch picker 전환되어도
    // 콜백 클로저는 시작 dispatch에 stopArrivals 기록 — 단일 운행 단위 일관성).
    const activeDispId = dispatch?.id || null;
    const activeRouteDepart = dispatch?.departTime || null;
    const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
    // 2026-06-01 — 운행 1회 단위 stopArrivals 기록 로그 초기화(이전 운행 잔존 차단).
    stopArrivalsLogRef.current = [];
    gpsRecordedRef.current = new Set();
    const id = startGPS({
      companyId, vehicleId: driver.vehicleId, vehicleNo: driver.vehicleNo || "",
      driverId: driver.id, driverName: driver.name || "",
      routeId: dispatch?.routeId || "", routeName: dispatch?.routeName || "",
      stops,
      // 노선 사전경로 — GPS 복구 시 진행률 기반 정류장 누락 백필(빈배열=직선 폴백).
      routePath: Array.isArray(routePath) ? routePath : [],
      // 최신 노선데이터 getter(2026-06-02) — startGPS 1회 캡처가 빈값이어도 ref 로 최신값을
      // 읽어 백필이 항상 작동(로드 타이밍 경합 무관). gps.js 가 매 감지 시 호출.
      getStops: () => stopsRef.current,
      getRoutePath: () => routePathRef.current,
      arriveRadiusM: arriveRadiusRef.current, // 회사 도착 감지 반경(미설정=100m)
      // 출발 전 도착 차단(2026-10-29 · lib/arrivalGate.js · 서버 recordStopArrival 과 같은 판정) —
      // 차고지→기점 길의 경로상 정류장을 도착 처리하지 않는다(처리하면 실제 운행 때 다시 감지 안 됨).
      canRecordStop: (stop, idx, viaBackfill) => !preDepartureBlocked({
        departMs: kstDepartMs(todayStr, activeRouteDepart), nowMs: Date.now(),
        isOrigin: idx === 0, viaBackfill: !!viaBackfill,
        originArrived: originArrivedNow(),
      }),
      onStopReached: async (stop, dist, viaBackfill) => {
        // 백필이 한 콜백에서 여러 정류장을 순차 호출할 수 있음 → Math.max로 역행 방지
        // (옛 정류장 인덱스가 현재값을 끌어내리지 않도록).
        const newIdx = stops.findIndex(s => s.id === stop.id);
        if (newIdx >= 0) setCurrentStopIdx(prev => Math.max(prev, newIdx));
        sendNotification(stop, dist);
        // ── stopArrivals 기록(CF 위임, 2026-06-01) ─────────────────────────────
        // 기존 클라이언트 updateDoc 경로는 rules driver 본인 검증(drivers.uid 매핑) 통과
        // 실패 시 silent fail — 2026-06-01 진단 JSON 분석으로 stopArrivals 갱신 0건
        // 확정 → CF `recordStopArrival` 위임으로 본질 우회(Admin SDK 가 rules 우회).
        // 멱등 가드는 CF 측이 수행(alreadyExists 반환). 결과는 stopArrivalsLogRef 에
        // 누적해 다음 진단 JSON 에 첨부 → 어느 정류장이 어떻게 기록됐는지 추적.
        const ts = Date.now();
        if (!activeDispId) {
          stopArrivalsLogRef.current.push({
            stopId: stop.id, ok: false, error: "activeDispId null", viaBackfill: !!viaBackfill, ts,
          });
          if (stopArrivalsLogRef.current.length > 40) stopArrivalsLogRef.current.shift();
          return;
        }
        const plannedAt = planTimeForStop(activeRouteDepart, stop.offsetMin);
        // delaySec 추정: plannedAt(오늘 ms) - now. 클라 추정값(서버는 actualAt에 serverTimestamp).
        let delaySec = null;
        if (plannedAt && typeof stop.offsetMin === "number") {
          const m = plannedAt.match(/^(\d{2}):(\d{2})$/);
          if (m) {
            const planDate = new Date(); planDate.setHours(+m[1], +m[2], 0, 0);
            delaySec = Math.round((Date.now() - planDate.getTime()) / 1000);
          }
        }
        try {
          const callable = httpsCallable(functions, "recordStopArrival");
          const res = await callable({
            companyId,
            date: todayStr,
            dispatchId: activeDispId,
            stopId: stop.id,
            plannedAt: plannedAt || null,
            delaySec,
            viaBackfill: !!viaBackfill,
          });
          stopArrivalsLogRef.current.push({
            stopId: stop.id,
            ok: true,
            alreadyExists: !!(res && res.data && res.data.alreadyExists),
            viaBackfill: !!viaBackfill,
            ts,
          });
        } catch (e) {
          stopArrivalsLogRef.current.push({
            stopId: stop.id,
            ok: false,
            error: e?.message || String(e),
            code: e?.code || null,
            viaBackfill: !!viaBackfill,
            ts,
          });
          console.warn("[BusLink] stopArrivals 기록 실패:", e?.code, e?.message);
        }
        if (stopArrivalsLogRef.current.length > 40) stopArrivalsLogRef.current.shift();
      },
      onGpsError: (err) => {
        // err.code 1=PERMISSION_DENIED, 3=TIMEOUT — 화면 안내(작업4 권한 UI와 연동)
        setGpsStatus(err.code === 1 ? "권한" : "확보중");
      },
    });
    // 로컬 state는 즉시 반영(버튼 응답성 보장 — 통신 불량과 무관).
    setWatchId(id);
    setDriving(true);
    // ETA 자동 진단 로깅 runId 산출(2026-05-29) — 운행 1회 단위.
    setRunId(buildRunId({ driverId: driver.id, vehicleId: driver.vehicleId, startedAtMs: Date.now() }));
    // ✅ 탑승 QR 토큰 최초 생성 — passenger-qr 모드(역방향: 직원 발행/기사 스캔)는 skip.
    // 2026-05-28: await 제거 — 통신 불량 시 hang으로 운행 시작 자체가 지연되던 결함 차단.
    // refreshToken 내부에 try/catch 보호장치 있음(이미). 5분 자동 갱신 인터벌은 무관하게 등록.
    if (boardingMode !== "passenger-qr") {
      refreshToken(driver, dispatch);
      tokenTimerRef.current = setInterval(() => refreshToken(driver, dispatch), 5 * 60 * 1000);
    }
  };

  const handleStop = async () => {
    // ── 2026-05-28 버튼 응답성 수정 ─────────────────────────────────────────
    // 통신 불량 시 `await clearGPS`/`await updateDoc`이 hang하면 setDriving(false) 등
    // 로컬 state 갱신이 영영 실행 안 됨 → "운행 종료 버튼 안 눌림". 해법 = 로컬 state·
    // stopGPS는 즉시, Firestore 쓰기는 fire-and-forget(`.catch` 로 graceful).
    const watchToStop = watchId;
    const vehId = driver?.vehicleId;
    // 로컬 state 즉시 갱신(통신과 무관 — 버튼 누름 즉시 UI 반응).
    setDriving(false);
    setWatchId(null);
    setGpsStatus("");
    setCurrentStopIdx(-1);
    // ETA 자동 진단 로깅 runId 해제(2026-05-29) — interval은 effect cleanup으로 자동 정지.
    setRunId(null);
    lastVehiclePosRef.current = null;
    lastVehicleSpeedRef.current = null;
    // 2026-06-01 — stopArrivals 기록 로그 정리(다음 운행 시 재초기화 + 누적 차단).
    stopArrivalsLogRef.current = [];
    gpsRecordedRef.current = new Set();
    setBoardingToken(null);
    setQrUrl(null);
    setActiveTab("운행");
    if (wakeLockRef.current) {
      try { wakeLockRef.current.release(); } catch {}
      wakeLockRef.current = null;
    }
    if (tokenTimerRef.current) { clearInterval(tokenTimerRef.current); tokenTimerRef.current = null; }
    // GPS watch 정지는 동기 — 즉시 안전.
    if (watchToStop != null) stopGPS(watchToStop);
    // Firestore 쓰기는 background — 실패해도 사용자는 이미 "운행 종료" 인지 가능.
    // 2026-07-28: 발행한 프라미스를 반환한다. "운행 종료" 버튼 경로는 이 반환값을 무시하므로
    // 위 회귀 가드(버튼 응답성)는 그대로이고, 로그아웃 경로만 이걸 기다렸다가 signOut 한다.
    const pending = [];
    if (vehId) {
      pending.push(clearGPS({ companyId, vehicleId: vehId })
        .catch(e => console.warn("[BusLink] clearGPS 실패(통신):", e.message)));
    }
    if (driver?.id) {
      pending.push(updateDoc(doc(db, "companies", companyId, "drivers", driver.id), {
        status: "대기", endedAt: new Date().toISOString(),
      }).catch(e => console.warn("[BusLink] drivers 상태 update 실패(통신):", e.message)));
    }
    return pending;
  };

  const refreshToken = async (drv, disp) => {
    try {
      const tokenId = await createBoardingToken({
        companyId,
        routeId: disp?.routeId || "",
        routeName: disp?.routeName || "",
        vehicleId: drv.vehicleId,
        vehicleNo: drv.vehicleNo || "",
        driverId: drv.id,
      });
      const url = getBoardingUrl(tokenId);
      setBoardingToken(tokenId);
      setQrUrl(url);
      // ✅ qrcode 라이브러리로 로컬 생성 (외부 API 의존성 없음, 오프라인 동작)
      const dataUrl = await QRCode.toDataURL(url, {
        width: 220,
        margin: 2,
        color: { dark: "#0B1A2E", light: "#FFFFFF" },
        errorCorrectionLevel: "M",
      });
      setQrDataUrl(dataUrl);
    } catch (e) {
      console.warn("[BusLink] 토큰 생성 실패:", e.message);
    }
  };

  const handleLogout = async () => {
    if (driving) {
      // ⚠ 2026-07-28 — 예전엔 handleStop() 직후 곧바로 signOut 했다. 운행 종료 쓰기 2건은
      // fire-and-forget 이라 아직 전송 중인 상태에서 인증이 끊기면 permission-denied 로
      // 유실된다 → "로그아웃했는데 관제에는 계속 운행중"(prod 실측: gps 문서는 지워졌는데
      // drivers.status 만 18일째 "운행중" 으로 남은 기사). 그래서 signOut 전에 착지를 기다린다.
      // 통신 불량으로 hang 해도 로그아웃 자체는 막지 않도록 3초에서 끊는다.
      const pending = await handleStop();
      if (pending && pending.length) {
        await Promise.race([
          Promise.allSettled(pending),
          new Promise(resolve => setTimeout(resolve, 3000)),
        ]);
      }
    }
    signOut(auth);
  };

  // 오늘 날짜 — 인사 영역 표시용(시각 전용, 로직 무관)
  const todayLabel = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
  }).format(new Date());

  if (loading) return (
    <div style={S.fullCenter}>
      <InstallPrompt />
      <div style={{ color: "var(--color-primary)", fontSize: 16, fontWeight: 600 }}>로딩 중...</div>
    </div>
  );

  if (error) return (
    <div style={{ ...S.fullCenter, flexDirection: "column", gap: 16 }}>
      <InstallPrompt />
      <div style={{ color: "var(--color-destructive)", fontSize: 15, textAlign: "center", whiteSpace: "pre-line", fontWeight: 600 }}>{error}</div>
      {/* 복구형 에러 화면(2026-06-22) — 로그인 직후 일시적 경합으로 init 이 실패해도
          재로그인 없이 "다시 시도"로 진입 가능. autoRetriedRef 리셋 후 init 재실행. */}
      <div style={{ display: "flex", gap: 10 }}>
        <button
          style={{ ...S.primaryBtn, maxWidth: 160 }}
          onClick={() => { autoRetriedRef.current = false; init(); }}
        >
          다시 시도
        </button>
        <button style={S.outlineBtn} onClick={() => signOut(auth)}>로그아웃</button>
      </div>
    </div>
  );

  return (
    <div style={S.container}>
      <InstallPrompt />
      {/* 빌드 버전 뱃지(2026-06-05) — 폰이 옛 캐시 번들을 도는지 눈으로 즉시 확인.
          이 날짜가 최신이 아니면 새 빌드 미수신 → 캐시 정리(앱 삭제·사이트 데이터 삭제) 필요. */}
      <div style={{ position: "fixed", bottom: 4, right: 6, zIndex: 9999, fontSize: 10, color: "rgba(0,0,0,0.5)", background: "rgba(255,255,255,0.75)", padding: "1px 6px", borderRadius: 8, pointerEvents: "none" }}>
        v{DRIVER_BUILD}
      </div>
      <div style={S.card}>
        {/* 헤더 */}
        <div style={S.header}>
          <BusLinkLogo size={18} sub="기사" />
          <button style={S.logoutBtn} onClick={handleLogout}>로그아웃</button>
        </div>

        {/* 권한 경고 배너 + 앱 설치 버튼 (운행 화면 상단)
            🔴 스토어 앱(«BusLink 운행») 안에서는 감춘다 — 그 WebView 는 위치 권한을 OS 에 허용해도
            navigator.permissions 가 늘 «prompt» 를 돌려줘 허용 상태에서도 배너가 떴다(2026-10-21 폴드7).
            앱의 위치 권한은 운행 시작 때 위치 플러그인이, 알림 권한은 첫 실행 때 MainActivity 가 묻는다. */}
        {!isNativeApp() && <PermissionGate />}

        {/* GPS 신호 안내 — 운행 시작 시 측위 확보 전/권한 미허용일 때만 */}
        {gpsStatus && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "9px 12px", borderRadius: "var(--radius-12)",
            background: gpsStatus === "권한" ? "#FDECEC" : "var(--color-primary-soft)",
            border: gpsStatus === "권한" ? "1px solid var(--color-destructive)" : "1px solid var(--color-primary)",
            color: gpsStatus === "권한" ? "var(--color-destructive)" : "var(--color-primary-deep)",
            fontSize: 13, fontWeight: 700,
          }}>
            <StatusDot tone={gpsStatus === "권한" ? "danger" : "primary"} size={8} pulse />
            {gpsStatus === "권한" ? "GPS 권한을 허용해주세요" : "GPS 신호 확보 중…"}
          </div>
        )}

        {/* 인사 — 길안내에서는 감춘다(아래 navFocus 참고) */}
        {!navFocus && (
          <div>
            <div style={S.greetingDate}>{todayLabel}</div>
            <div style={S.greetingName}>
              {driver?.name ? `${driver.name} 기사님, 안녕하세요` : "안녕하세요"}
            </div>
          </div>
        )}

        {/* 배차 정보 — 그라데이션 히어로 카드.
            🔴 길안내 탭에서는 감춘다(2026-08-05 way "길안내할 때는 최대한 지도 중심으로,
            상단 메뉴도 최소화"). 다음 정류장·남은거리·예정시각은 지도 위 칩이 이미 보여준다. */}
        {!navFocus && (dispatch ? (
          <div style={S.heroCard}>
            {/* 버스 정면 선화 워터마크 — 예전 흰 사각형+원 덩어리는 «이미지가 빠진 자리»처럼 보였다(2026-10-21 way) */}
            <svg viewBox="0 0 120 120" style={S.heroBusIcon} fill="none" stroke="#fff"
              strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="22" y="12" width="76" height="84" rx="16" />
              <rect x="40" y="19" width="40" height="6" rx="3" />
              <rect x="30" y="31" width="60" height="32" rx="7" />
              <path d="M22 40h-6v14M98 40h6v14" />
              <circle cx="37" cy="78" r="5" /><circle cx="83" cy="78" r="5" />
              <path d="M52 80h16" />
              <path d="M32 96v9h12v-9M76 96v9h12v-9" />
            </svg>
            <div style={{ position: "relative" }}>
              <div style={S.heroTop}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Pill tone="dark" dot style={{ background: "rgba(255,255,255,0.18)", color: "#fff" }}>
                    오늘 배차
                  </Pill>
                  {dispatches.length > 1 && (
                    <button
                      onClick={() => { setDispatchQuery(""); setPickerOpen(true); }}
                      style={S.dispatchChangeBtn}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="repeat" size={13} stroke={2} /> 배차 변경 ({dispatches.length}건)</span>
                    </button>
                  )}
                </div>
                {driving && (
                  <span style={S.heroLiveBadge}>
                    <StatusDot tone="positive" size={6} pulse /> 운행중
                  </span>
                )}
              </div>
              <div style={S.heroRoute}>{dispatch.routeName}</div>
              <div style={S.heroMetaRow}>
                <div><span style={S.heroMetaLabel}>차량 </span><span style={S.heroMetaVal}>{dispatch.vehicleNo || "-"}</span></div>
                {dispatch.departTime && (
                  <div><span style={S.heroMetaLabel}>출발 </span><span style={S.heroMetaVal}>{dispatch.departTime}</span></div>
                )}
              </div>

              {/* 정류장 진행 — 운행 중이고 현재 정류장이 잡혔을 때만 실제 데이터로 */}
              {driving && stops.length > 0 && (() => {
                const total = stops.length;
                const done = currentStopIdx < 0 ? 0 : Math.min(currentStopIdx + 1, total);
                const next = stops[currentStopIdx + 1] || stops[currentStopIdx] || null;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                const nextEst = next ? estByStopId[next.id] : null;
                const lab = nextEst ? formatDelayLabel(nextEst.delaySec) : { tone: "mute", label: "" };
                const labColor = lab.tone === 'danger' ? '#FFD8D8'
                  : lab.tone === 'warn' ? '#FFE7BF'
                  : '#CFEFE0';
                return (
                  <div style={S.heroProgress}>
                    <div style={S.heroProgressTop}>
                      <span>다음 정류장</span>
                      <span style={S.heroProgressCount}>{done}/{total} 정류장</span>
                    </div>
                    <div style={S.heroNextStop}>{next ? next.name : "운행 시작"}</div>
                    {nextEst && nextEst.plannedAt && (
                      <div style={{ fontSize: 17, color: "#fff", marginTop: 8, fontWeight: 800, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                        <span style={{ opacity: 0.85, fontWeight: 700 }}>계획</span>
                        <span style={{ fontSize: 20 }}>{nextEst.plannedAt}</span>
                        {nextEst.estimatedAt && nextEst.estimatedAt !== nextEst.plannedAt && (
                          <>
                            <span style={{ opacity: 0.55 }}>·</span>
                            <span style={{ opacity: 0.85, fontWeight: 700 }}>예상</span>
                            <span style={{ fontSize: 20 }}>{nextEst.estimatedAt}</span>
                          </>
                        )}
                        {lab.label && lab.tone !== 'mute' && (
                          <span style={{ background: "rgba(255,255,255,0.22)", border: `2px solid ${labColor}`, color: labColor, borderRadius: 999, padding: "4px 13px", fontSize: 15, fontWeight: 800 }}>
                            {lab.label}
                          </span>
                        )}
                      </div>
                    )}
                    <div style={S.heroBar}>
                      <div style={{ ...S.heroBarFill, width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : (
          <div style={S.emptyDispatch}>
            <Icon name="bus" size={28} />
            <div style={{ marginTop: 8 }}>오늘 배차된 노선이 없습니다</div>
          </div>
        ))}

        {/* GPS 단말 차량 안내 배너 — 앱이 위치를 전송하지 않고 단말로 서버 자동 추적. */}
        {driving && deviceGps && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "var(--color-primary-soft)", color: "var(--color-primary-deep)",
            borderRadius: 10, padding: "12px 14px", fontSize: 13, fontWeight: 600,
          }}>
            이 차량은 GPS 단말로 자동 추적됩니다 — 앱 위치 전송 안 함
          </div>
        )}

        {/* 라이브 상태 스트립 — GPS 전송 상태(실제 driving / Wake Lock 만, 가짜 수치 없음).
            길안내에서는 감춘다 — 운전 중에 계속 볼 정보가 아니고 지도 높이를 크게 먹는다.
            (GPS 가 실제로 끊기면 지도 위 "위치를 확인하는 중입니다" 칩이 따로 뜬다) */}
        {driving && !navFocus && (
          <div style={S.statStrip}>
            <div style={S.miniStat}>
              <div style={S.miniStatLabel}>상태</div>
              <div style={{ ...S.miniStatVal, color: "var(--color-positive)" }}>운행중</div>
            </div>
            <div style={S.miniStat}>
              <div style={S.miniStatLabel}>GPS</div>
              <div style={{ ...S.miniStatVal, color: "var(--color-positive)" }}>{deviceGps ? "단말 추적" : "전송중"}</div>
            </div>
            <div style={S.miniStat}>
              <div style={S.miniStatLabel}>화면</div>
              <div style={S.miniStatVal}>{deviceGps ? "—" : ("wakeLock" in navigator ? "켜짐 유지" : "기본")}</div>
            </div>
          </div>
        )}

        {/* 운행 시작 버튼 (미운행 시) — 노선 데이터(stops+routePath) 로드 완료 후에만 활성화.
            로드 전 시작 시 startGPS 가 빈 routePath 를 캡처해 도착 백필이 꺼지는 경합 차단(2026-06-02). */}
        {!driving && (
          <button
            style={{ ...S.primaryBtn, ...(dispatch && routeDataReady ? {} : S.primaryBtnDisabled) }}
            onClick={handleStart}
            disabled={!dispatch || !routeDataReady}
          >
            <Icon name="play" size={18} /> {dispatch && !routeDataReady ? "노선 불러오는 중…" : "운행 시작"}
          </button>
        )}

        {/* 탭 전환 — 항상 표시. passenger-qr 모드는 라벨이 'QR 스캔'(기사가 스캔측).
            NFC 탭(2026-07-22)은 **지원 여부와 무관하게 항상 노출**한다(2026-07-22 정정):
            기사 단말은 안드로이드로 통제되므로 대부분 동작하고, 미지원인 드문 경우
            (PC 접속·삼성 인터넷 등 크롬 아닌 브라우저·아이패드)에는 탭 안에서 이유와
            해결법을 안내해야 한다. 탭을 숨기면 그 안내가 도달 불가능해져 기사는
            "NFC 가 왜 없지?"만 겪는다. */}
        <div style={S.tabRow}>
          {["운행", "길안내", "탑승 QR", "NFC"].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ ...S.tabBtn, ...(activeTab === tab ? S.tabBtnActive : S.tabBtnIdle) }}>
              {tab === "탑승 QR" && <Icon name="qr" size={16} />}{tab === "NFC" && <Icon name="phone" size={16} />}{tab === "길안내" && <Icon name="route" size={16} />}{" "}
              {tab === "탑승 QR" && boardingMode === "passenger-qr" ? "QR 스캔" : tab}
            </button>
          ))}
        </div>

        {/* ─ 길안내 탭 (2026-07-28) ─ */}
        {activeTab === "길안내" && (
          <DriverNavGuide
            companyId={companyId}
            routeId={dispatch?.routeId}
            stops={stops}
            routePath={routePath}
            currentStopIdx={currentStopIdx}
            livePos={liveVehiclePos}
            estByStopId={estByStopId}
            deviceGps={deviceGps}
            compact
          />
        )}

        {/* ─ 운행 탭: 정류장 현황 ─
            ⚠ 미운행 시엔 탭과 무관하게 목록을 보여주던 기존 동작 유지(회귀 0).
               길안내 탭에서만 목록을 감춘다(지도가 그 자리를 대신하므로). */}
        {activeTab !== "길안내" && (!driving || activeTab === "운행") && stops.length > 0 && (
          <div style={S.listCard}>
            <div style={S.listHeader}>
              <span style={S.listTitle}>오늘 운행 정류장</span>
              <span style={S.listCount}>{stops.length}개소</span>
            </div>
            {/* 정류장 리스트 — 자체 스크롤 컨테이너로 가둬야 `scrollIntoView({block:'center'})`
                가 페이지 전체가 아닌 리스트 내부에서 카지노 릴처럼 동작.
                stops 5개 이하: maxHeight 의미 거의 없음(자연 높이 < 60vh).
                stops 6개+: 스크롤 활성 + 현재 정류장 중앙 정렬.
                스크롤바 시각 정리는 `data-stop-list` + index.css(인라인 불가). */}
            <div data-stop-list style={{
              display: "flex", flexDirection: "column", gap: 2, marginTop: 8,
              maxHeight: "60vh", overflowY: "auto",
              WebkitOverflowScrolling: "touch", // iOS 부드러운 관성 스크롤
            }}>
              {stops.map((stop, i) => {
                const isDone = i < currentStopIdx;
                const isCurrent = i === currentStopIdx;
                const isNext = i === currentStopIdx + 1;
                const est = estByStopId[stop.id];
                const arrived = est && est.status === 'arrived';
                // 표시용 지연(기사앱, 2026-06-04 사용자 결정) — 도착=실측지연(actual−계획) /
                // 운행 중 미도착이라도 계획시각이 지났으면 (현재−계획)을 누적 표시 → 늦게
                // 출발해도 "정시"로 보이던 결함 차단(도착 감지와 무관하게 출발 지연 가시화).
                let dispDelaySec = est ? est.delaySec : null;
                if (est && !arrived && driving && est.plannedAt) {
                  const hm = /^(\d{2}):(\d{2})$/.exec(est.plannedAt);
                  if (hm) {
                    const pd = new Date(); pd.setHours(+hm[1], +hm[2], 0, 0);
                    if (nowTick > pd.getTime()) dispDelaySec = Math.round((nowTick - pd.getTime()) / 1000);
                  }
                }
                const lab = est ? formatDelayLabel(dispDelaySec) : { tone: "mute", label: "" };
                const labColor = lab.tone === 'danger' ? 'var(--color-destructive)'
                  : lab.tone === 'warn' ? 'var(--color-cautionary)'
                  : 'var(--color-positive)';
                const labBg = lab.tone === 'danger' ? '#FDECEC'
                  : lab.tone === 'warn' ? '#FFF3E0'
                  : '#E6F7EB';
                // 행 배경 — 도착(arrived)=positive 카드, 현재=primary 카드, 그 외=투명
                const rowBg = arrived ? '#F0FAF4'
                  : isCurrent ? 'var(--color-primary-soft)'
                  : 'transparent';
                const rowBorder = arrived ? '1px solid #B6E6C6'
                  : isCurrent ? '1px solid var(--color-primary)'
                  : '1px solid transparent';
                return (
                  <div key={stop.id}
                    ref={isCurrent ? currentStopRowRef : null}
                    style={{
                      ...S.stopRow,
                      background: rowBg, border: rowBorder,
                      // 현재 정류장 글로우 — 어르신 기사 시인성
                      boxShadow: isCurrent ? "0 0 0 4px rgba(0,102,255,0.18), 0 4px 14px rgba(0,102,255,0.20)" : "none",
                      transition: "box-shadow .25s ease, background .25s ease",
                      // 현재 정류장은 살짝 padding 더(더 큰 카드 느낌)
                      padding: isCurrent ? "18px 14px" : "14px 10px",
                    }}>
                    {/* 현재 정류장 dot은 펄스 ring으로 강조(buspulse 재사용) */}
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <div style={{
                        ...S.stopDot,
                        // 현재 정류장은 한 단계 더 큼
                        width: isCurrent ? 18 : 14, height: isCurrent ? 18 : 14,
                        background: arrived ? "var(--color-positive)"
                          : isCurrent ? "var(--color-primary)"
                          : isDone ? "var(--color-primary)" : "#fff",
                        border: `3px solid ${arrived ? "var(--color-positive)"
                          : (isCurrent || isDone) ? "var(--color-primary)"
                          : "var(--color-atomic-coolNeutral-90)"}`,
                      }} />
                      {isCurrent && (
                        <div style={{
                          position: "absolute", inset: -3,
                          borderRadius: "50%", background: "var(--color-primary)",
                          animation: "buspulse 2s ease-out infinite",
                          pointerEvents: "none",
                        }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* 정류장 이름 — 통과한 정류장(도착 제외)은 흐리되, 시각·지연 정보는 또렷 유지 */}
                      <div style={{
                        ...S.stopName,
                        color: isCurrent ? "var(--color-primary-deep)" : "var(--color-label)",
                        opacity: (isDone && !arrived) ? 0.55 : 1,
                      }}>
                        {stop.name}
                      </div>
                      {/* 계획시각 + 지연(크게) — 기사앱은 "계획 대비 지연"이 본질(2026-06-04 사용자 결정).
                          예상 도착시각은 아래 작게 참고용. 운행 중 도착 전이라도 계획시각이 지나면
                          dispDelaySec(현재−계획)로 "지연 N분"을 누적 표시(출발 지연 가시화). */}
                      {est && (est.plannedAt || arrived || (driving && est.estimatedAt)) && (
                        <div style={{ marginTop: 5 }}>
                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700 }}>
                            {est.plannedAt && (
                              <>
                                <span style={{ color: "var(--color-label-mute)" }}>계획</span>
                                <span style={{ color: "var(--color-label)", fontWeight: 800, fontSize: 18 }}>{est.plannedAt}</span>
                              </>
                            )}
                            {arrived && est.estimatedAt && (
                              <span style={{ color: 'var(--color-positive)', fontWeight: 800, fontSize: 16 }}>· 도착 {est.estimatedAt}</span>
                            )}
                            {(arrived || driving) && est.plannedAt && lab.label && lab.tone !== 'mute' && (
                              <span style={{
                                background: labBg, color: labColor, fontWeight: 800, fontSize: 16,
                                padding: "4px 12px", borderRadius: 999, border: `1.5px solid ${labColor}`,
                              }}>
                                {lab.label}
                              </span>
                            )}
                          </div>
                          {/* 예상 도착시각 — 작게 참고용(기사앱은 지연이 본질, 예상은 보조). */}
                          {!arrived && driving && est.estimatedAt && (
                            <div style={{ fontSize: 12, color: "var(--color-label-mute)", marginTop: 3 }}>
                              예상 도착 {est.estimatedAt} <span style={{ opacity: 0.7 }}>(참고)</span>
                            </div>
                          )}
                        </div>
                      )}
                      {stop.address && (
                        <div style={{ ...S.stopAddr, opacity: (isDone && !arrived) ? 0.55 : 1 }}>
                          {stop.address}
                        </div>
                      )}
                    </div>
                    {isCurrent && <span style={S.tagCurrent}>현재</span>}
                    {isNext && <span style={S.tagNext}>다음</span>}
                  </div>
                );
              })}
            </div>

            {/* [DIAG-ETA 제거예정] 진단 오버레이 — driving + 운행 탭 한정.
                정류장별 ETA·source·delay를 표로 가시화해 사용자가 폰으로 직접 캡쳐.
                다음 커밋에 grep "[DIAG-ETA" 1줄로 통째 제거. */}
            {driving && (
              <div style={{ marginTop: 10 }}>
                <button
                  onClick={() => setDiagOpen(v => !v)}
                  style={{
                    fontSize: 11, padding: "5px 10px",
                    borderRadius: 8, border: "1px dashed var(--color-label-mute)",
                    background: "transparent", color: "var(--color-label-mute)",
                    cursor: "pointer", fontFamily: "inherit",
                  }}>
                  진단 {diagOpen ? "▲" : "▼"}
                </button>
                {diagOpen && (
                  <div style={{
                    marginTop: 6, padding: 8, borderRadius: 8,
                    background: "#FFFBEA", border: "1px dashed #C99A2E",
                    fontSize: 10, fontFamily: "monospace", lineHeight: 1.4,
                    color: "#3a2e08", overflowX: "auto", whiteSpace: "nowrap",
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>[DIAG-ETA] 정류장 ETA 진단</div>
                    {stopEstimates.map((e, i) => {
                      const stop = stops.find(s => s.id === e.stopId);
                      const name = stop?.name || e.stopId;
                      const delayMin = e.delaySec != null ? Math.round(e.delaySec / 60) : null;
                      const delayLab = delayMin == null ? "—"
                        : delayMin === 0 ? "0분"
                        : delayMin > 0 ? `+${delayMin}분`
                        : `${delayMin}분`;
                      return (
                        <div key={e.stopId}>
                          [{i}] {name.length > 8 ? name.slice(0, 8) + "…" : name}
                          {" | plan "}{e.plannedAt || "—"}
                          {" | est "}{e.estimatedAt || "—"}
                          {" | delay "}{delayLab}
                          {" | "}{e.status}
                          {" | "}{e.source}
                        </div>
                      );
                    })}
                    <div style={{ marginTop: 4, opacity: 0.7 }}>
                      currentStopIdx={currentStopIdx} | stops={stops.length}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* [/DIAG-ETA] */}
          </div>
        )}

        {/* ─ 탑승 QR 탭 — boardingMode 분기 ─ */}
        {/* (a) driver-qr 모드(기본): 기사 발행 → 직원 스캔 (기존 흐름 100% 보존) */}
        {activeTab === "탑승 QR" && boardingMode !== "passenger-qr" && !driving && (
          <div style={S.qrNotice}>
            <Icon name="qr" size={26} />
            <div>
              <div style={S.qrNoticeTitle}>운행 시작 후 QR이 활성화됩니다</div>
              <div style={S.qrNoticeSub}>운행 시작 버튼을 누르면 탑승 QR이 자동 생성됩니다</div>
            </div>
          </div>
        )}
        {activeTab === "탑승 QR" && boardingMode !== "passenger-qr" && driving && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            {qrUrl ? (
              <>
                <div style={S.qrGuide}>승객이 아래 QR을 스캔하면 탑승이 기록됩니다</div>
                {/* QR 코드 이미지 - qrcode 라이브러리 (로컬 생성, 오프라인 동작) */}
                <div style={S.qrBox}>
                  {qrDataUrl
                    ? <img src={qrDataUrl} alt="탑승 QR" width={220} height={220} style={{ display: "block", borderRadius: 8 }} />
                    : <div style={S.qrPlaceholder}>생성 중...</div>
                  }
                </div>
                <div style={S.qrRouteBox}>
                  <div style={S.qrRouteLabel}>노선</div>
                  <div style={S.qrRouteVal}>{dispatch?.routeName}</div>
                </div>
                <div style={S.qrAutoNote}>QR코드는 5분마다 자동 갱신됩니다</div>
                <button onClick={() => refreshToken(driver, dispatch)} style={S.refreshBtn}>
                  QR 즉시 갱신
                </button>
              </>
            ) : (
              <div style={S.qrGuide}>QR 생성 중...</div>
            )}
          </div>
        )}

        {/* (b) passenger-qr 모드: 직원 발행 → 기사 스캔 (2026-05-27 역방향) */}
        {activeTab === "탑승 QR" && boardingMode === "passenger-qr" && !driving && (
          <div style={S.qrNotice}>
            <Icon name="qr" size={26} />
            <div>
              <div style={S.qrNoticeTitle}>운행 시작 후 스캔이 활성화됩니다</div>
              <div style={S.qrNoticeSub}>직원이 발행한 QR을 스캔하여 탑승을 기록합니다</div>
            </div>
          </div>
        )}
        {activeTab === "탑승 QR" && boardingMode === "passenger-qr" && driving && (
          <DriverPassengerScan
            companyId={companyId}
            driver={driver}
            dispatch={dispatch}
            currentStop={(currentStopIdx >= 0 && stops[currentStopIdx]) ? stops[currentStopIdx] : null}
          />
        )}

        {/* ─ NFC 탭 (2026-07-22) — QR 과 동일하게 운행 중에만 활성 ─
            서버가 "오늘 이 차량 배차"로 노선을 해석하므로 배차·운행 상태가 전제. */}
        {activeTab === "NFC" && !driving && (
          <div style={S.qrNotice}>
            <span style={{ display: "inline-flex", color: "var(--color-primary)" }}><Icon name="phone" size={24} stroke={1.8} /></span>
            <div>
              <div style={S.qrNoticeTitle}>운행 시작 후 태깅이 활성화됩니다</div>
              <div style={S.qrNoticeSub}>운행 시작 버튼을 누른 뒤 사원증을 태그해주세요</div>
            </div>
          </div>
        )}
        {activeTab === "NFC" && driving && (
          <DriverNfcTag companyId={companyId} driver={driver} dispatch={dispatch} />
        )}
      </div>

      {/* 하단 운행 종료 바 — 운행 중에만 (목업: 검정 sticky) */}
      {driving && (
        <div style={S.bottomBar}>
          <button style={S.endBtn} onClick={handleStop}>
            <StatusDot tone="positive" size={8} pulse /> 운행 중 — 종료
          </button>
        </div>
      )}

      {/* ── 배차 선택 모달 — EmployeeApp routePicker 패턴(풀모달 오버레이·검색·카드 리스트) ── */}
      {pickerOpen && (
        <div onClick={() => setPickerOpen(false)} style={S.pickerBack}>
          <div onClick={e => e.stopPropagation()} style={S.pickerModal}>
            <div style={S.pickerHead}>
              <div style={{ width: 36, height: 4, background: "var(--color-line)", borderRadius: 2, margin: "0 auto 12px" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 15, fontWeight: 800 }}>오늘 배차 선택 ({dispatches.length}건)</span>
                <button onClick={() => setPickerOpen(false)} style={S.pickerClose}>✕</button>
              </div>
              {dispatches.length > 6 && (
                <input
                  style={{ ...S.pickerSearch, marginTop: 10 }}
                  placeholder="노선·차량·시간·거래처 검색"
                  value={dispatchQuery}
                  onChange={e => setDispatchQuery(e.target.value)}
                  autoFocus
                />
              )}
            </div>
            <div style={S.pickerBody}>
              {filteredDispatches.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--color-label-mute)", fontSize: 13 }}>
                  {dispatches.length === 0 ? "오늘 배차된 노선이 없습니다" : "검색 결과가 없습니다"}
                </div>
              ) : filteredDispatches.map(d => {
                const cur = d.id === activeDispatchId;
                return (
                  <div
                    key={d.id}
                    onClick={() => { setActiveDispatchId(d.id); setPickerOpen(false); }}
                    style={{ ...S.pickerCard, ...(cur ? S.pickerCardActive : {}) }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
                      <span style={S.pickerTime}>{d.departTime || "--:--"}</span>
                      <span style={S.pickerType}>{d.routeType || ""}</span>
                      {cur && <span style={S.pickerCurrent}>현재</span>}
                    </div>
                    <div style={S.pickerRoute}>{d.routeName || "노선?"}</div>
                    <div style={S.pickerMeta}>
                      {d.vehicleNo && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="bus" size={12} stroke={2} />{d.vehicleNo}</span>}
                      {d.partnerName && <span> · {d.partnerName}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// DriverApp 한정 인라인 스타일. 색/라운드/그림자는 tokens.css 변수 기반(라이트).
const S = {
  fullCenter: {
    minHeight: "100vh", background: "var(--color-bg-alt)", display: "flex",
    alignItems: "center", justifyContent: "center", fontFamily: "var(--font-base)",
  },
  outlineBtn: {
    background: "var(--color-bg)", border: "1px solid var(--color-line)",
    borderRadius: "var(--radius-8)", padding: "8px 20px", color: "var(--color-label)",
    cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 14,
  },

  // 배차 변경 — hero 카드 안의 작은 버튼(흰 반투명, 그라데이션 위 라이트 토큰만)
  dispatchChangeBtn: {
    background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.4)",
    borderRadius: "var(--radius-pill)", padding: "4px 11px", color: "#fff",
    fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
    backdropFilter: "blur(4px)",
  },

  // 배차 선택 모달 (EmployeeApp routePicker 패턴 차용)
  pickerBack: {
    position: "fixed", inset: 0, background: "var(--color-overlay)", zIndex: 200,
    display: "flex", flexDirection: "column", justifyContent: "flex-end",
  },
  pickerModal: {
    background: "var(--color-bg)", borderRadius: "20px 20px 0 0",
    width: "100%", maxHeight: "82dvh", display: "flex", flexDirection: "column",
    boxShadow: "var(--shadow-heavy)",
  },
  pickerHead: {
    padding: "14px 16px 12px", borderBottom: "1px solid var(--color-line)",
    flexShrink: 0,
  },
  pickerClose: {
    background: "var(--color-bg-soft)", border: "1px solid var(--color-line)",
    borderRadius: "var(--radius-8)", padding: "6px 12px",
    color: "var(--color-label-mute)", cursor: "pointer",
    fontFamily: "inherit", fontSize: 12,
  },
  pickerSearch: {
    width: "100%", padding: "9px 12px",
    border: "1px solid var(--color-line)", borderRadius: "var(--radius-8)",
    fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box",
  },
  pickerBody: { padding: "12px 16px 22px", overflowY: "auto", flex: 1 },
  pickerCard: {
    display: "block", padding: "12px 14px", marginBottom: 8,
    borderRadius: "var(--radius-12)", cursor: "pointer",
    border: "1px solid var(--color-line)", background: "var(--color-bg)",
  },
  pickerCardActive: {
    border: "1px solid var(--color-primary)", background: "var(--color-primary-soft)",
  },
  pickerTime: { fontSize: 13, fontWeight: 800, color: "var(--color-primary-deep)" },
  pickerType: {
    fontSize: 10, padding: "3px 9px", borderRadius: "var(--radius-pill)",
    background: "var(--color-bg-soft)", color: "var(--color-label-mute)", fontWeight: 600,
  },
  pickerCurrent: {
    fontSize: 10, padding: "3px 9px", borderRadius: "var(--radius-pill)",
    background: "var(--color-primary)", color: "#fff", fontWeight: 700, marginLeft: "auto",
  },
  pickerRoute: {
    fontSize: 14, fontWeight: 700, color: "var(--color-label)", marginBottom: 3,
    wordBreak: "keep-all",
  },
  pickerMeta: { fontSize: 11, color: "var(--color-label-mute)" },

  container: {
    minHeight: "100vh", background: "var(--color-bg-alt)", display: "flex",
    alignItems: "flex-start", justifyContent: "center", padding: 16,
    fontFamily: "var(--font-base)", color: "var(--color-label)", overflowY: "auto",
  },
  card: {
    background: "transparent", borderRadius: 0, padding: 0, width: "100%", maxWidth: 420,
    display: "flex", flexDirection: "column", gap: 14, marginTop: 8, marginBottom: 96,
  },

  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "4px 4px 0",
  },
  logoutBtn: {
    background: "var(--color-bg)", border: "1px solid var(--color-line)",
    borderRadius: "var(--radius-8)", padding: "6px 12px", color: "var(--color-label-mute)",
    fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
  },

  greetingDate: { fontSize: 13, color: "var(--color-label-mute)", fontWeight: 600, padding: "0 4px" },
  greetingName: {
    fontSize: 22, fontWeight: 800, marginTop: 2, letterSpacing: "-0.02em",
    color: "var(--color-label)", padding: "0 4px",
  },

  // 그라데이션 히어로 배차카드 (목업 #0066FF → #003DCC)
  heroCard: {
    padding: "20px 20px 18px", borderRadius: "var(--radius-20)",
    background: "linear-gradient(155deg, var(--color-primary) 0%, var(--color-primary-deep) 100%)",
    color: "#fff", position: "relative", overflow: "hidden",
    boxShadow: "var(--shadow-strong)",
  },
  heroBusIcon: { position: "absolute", right: -14, top: 6, width: 124, opacity: 0.2, pointerEvents: "none" },
  heroTop: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  heroLiveBadge: {
    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12,
    fontWeight: 700, color: "#fff",
  },
  heroRoute: {
    fontFamily: "var(--font-brand)", fontSize: 28, fontWeight: 800, marginTop: 14,
    letterSpacing: "-0.025em",
  },
  heroMetaRow: { display: "flex", gap: 16, marginTop: 10, fontSize: 13 },
  heroMetaLabel: { color: "rgba(255,255,255,0.6)" },
  heroMetaVal: { fontWeight: 700, fontFamily: "var(--font-mono)" },
  heroProgress: {
    marginTop: 18, padding: 14, background: "rgba(255,255,255,0.10)",
    borderRadius: "var(--radius-12)",
  },
  heroProgressTop: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.85)",
  },
  heroProgressCount: { fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 14 },
  heroNextStop: { fontSize: 24, fontWeight: 800, marginTop: 6, letterSpacing: "-0.01em" },
  heroBar: {
    marginTop: 10, height: 6, background: "rgba(255,255,255,0.18)",
    borderRadius: 3, overflow: "hidden",
  },
  heroBarFill: { height: "100%", background: "#fff", borderRadius: 3, transition: "width .4s ease" },

  emptyDispatch: {
    background: "var(--color-bg)", borderRadius: "var(--radius-16)", padding: "32px 20px",
    border: "1px solid var(--color-line)", textAlign: "center",
    color: "var(--color-label-mute)", fontSize: 14, fontWeight: 600,
    display: "flex", flexDirection: "column", alignItems: "center",
    boxShadow: "var(--shadow-emphasize)",
  },

  statStrip: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 },
  miniStat: {
    background: "var(--color-bg)", borderRadius: "var(--radius-12)", padding: "10px 12px",
    textAlign: "center", boxShadow: "var(--shadow-emphasize)",
  },
  miniStatLabel: { fontSize: 11, color: "var(--color-label-mute)", fontWeight: 600 },
  miniStatVal: {
    fontFamily: "var(--font-brand)", fontSize: 15, fontWeight: 800,
    color: "var(--color-label)", marginTop: 3,
  },

  primaryBtn: {
    border: "none", borderRadius: "var(--radius-16)", padding: "18px 0",
    background: "var(--color-primary)", color: "#fff", fontSize: 16, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center",
    justifyContent: "center", gap: 8, boxShadow: "var(--shadow-strong)",
  },
  primaryBtnDisabled: {
    background: "var(--color-atomic-coolNeutral-90)", color: "var(--color-label-mute)",
    cursor: "not-allowed", boxShadow: "none",
  },

  tabRow: { display: "flex", gap: 6 },
  tabBtn: {
    // 🔴 nowrap — 폴드 접은 화면(약 360px)에서 «QR 스캔»이 «QR 스/캔» 두 줄로 깨졌다(2026-10-21 실기기)
    flex: 1, minWidth: 0, padding: "10px 6px", borderRadius: "var(--radius-12)", border: "none",
    cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
  },
  tabBtnActive: { background: "var(--color-primary)", color: "#fff" },
  tabBtnIdle: { background: "var(--color-bg)", color: "var(--color-label-mute)", boxShadow: "var(--shadow-emphasize)" },

  listCard: {
    background: "var(--color-bg)", borderRadius: "var(--radius-16)", padding: "14px 16px",
    boxShadow: "var(--shadow-emphasize)",
  },
  listHeader: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  listTitle: { fontSize: 16, fontWeight: 800, color: "var(--color-label)" },
  listCount: { fontSize: 14, color: "var(--color-primary)", fontWeight: 700 },
  stopRow: { display: "flex", alignItems: "center", gap: 14, padding: "14px 10px", borderRadius: "var(--radius-12)" },
  stopDot: { width: 14, height: 14, borderRadius: "50%", flexShrink: 0 },
  stopName: {
    fontSize: 18, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    letterSpacing: "-0.01em",
  },
  stopAddr: {
    fontSize: 13, color: "var(--color-label-mute)", overflow: "hidden",
    textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2, fontWeight: 600,
  },
  tagCurrent: {
    fontSize: 13, background: "var(--color-primary-soft)", color: "var(--color-primary-deep)",
    borderRadius: 999, padding: "5px 12px", flexShrink: 0, fontWeight: 800,
  },
  tagNext: {
    fontSize: 13, background: "var(--color-atomic-orange-90)", color: "#B95300",
    borderRadius: 999, padding: "5px 12px", flexShrink: 0, fontWeight: 800,
  },

  qrNotice: {
    background: "var(--color-atomic-orange-90)", borderRadius: "var(--radius-12)",
    padding: "16px 18px", display: "flex", alignItems: "center", gap: 12, color: "#B95300",
  },
  qrNoticeTitle: { fontSize: 13, fontWeight: 700, marginBottom: 4 },
  qrNoticeSub: { fontSize: 12, color: "#B95300", opacity: 0.85 },
  qrGuide: { fontSize: 12, color: "var(--color-label-mute)", textAlign: "center" },
  qrBox: {
    background: "#fff", borderRadius: "var(--radius-16)", padding: 16,
    display: "inline-block", boxShadow: "var(--shadow-strong)",
  },
  qrPlaceholder: {
    width: 220, height: 220, display: "flex", alignItems: "center",
    justifyContent: "center", color: "var(--color-label-mute)", fontSize: 12,
  },
  qrRouteBox: {
    background: "var(--color-bg)", borderRadius: "var(--radius-12)", padding: "10px 16px",
    textAlign: "center", width: "100%", boxShadow: "var(--shadow-emphasize)",
  },
  qrRouteLabel: { fontSize: 11, color: "var(--color-label-mute)", marginBottom: 4 },
  qrRouteVal: { fontSize: 14, fontWeight: 700, color: "var(--color-primary)" },
  qrAutoNote: { fontSize: 11, color: "var(--color-cautionary)", textAlign: "center", fontWeight: 600 },
  refreshBtn: {
    border: "1px solid var(--color-line)", borderRadius: "var(--radius-12)",
    padding: "12px", color: "var(--color-label)", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit", background: "var(--color-bg)", width: "100%",
  },

  // 하단 운행 종료 바 (목업: 검정 sticky)
  bottomBar: {
    position: "fixed", left: 0, right: 0, bottom: 0, padding: "12px 16px 20px",
    background: "linear-gradient(to top, var(--color-bg-alt) 60%, rgba(247,247,248,0))",
    display: "flex", justifyContent: "center",
  },
  endBtn: {
    width: "100%", maxWidth: 420, padding: "18px 0", borderRadius: "var(--radius-16)",
    border: "none", background: "var(--color-label)", color: "#fff", fontSize: 16,
    fontWeight: 800, fontFamily: "inherit", boxShadow: "var(--shadow-heavy)",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    cursor: "pointer",
  },
};

// ─── 기사용 승객 QR 스캔 컴포넌트 (passenger-qr 모드, 2026-05-27) ─────
// 직원이 본인 폰에서 발행한 passengerTokens QR을 기사 폰 카메라로 스캔.
// EmployeeApp ScanTab 카메라 로직(getUserMedia+jsQR 루프) 패턴 차용.
// 성공 시 3초 success 토스트 → 자동 다음 스캔 모드(연속 탑승).
function DriverPassengerScan({ companyId, driver, dispatch, currentStop }) {
  const [step, setStep] = useState("ready"); // ready|scanning|processing|success|error
  const [errMsg, setErrMsg] = useState("");
  const [boarded, setBoarded] = useState(null); // { empNo, name } 성공 토스트용
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const activeRef = useRef(false);

  // 언마운트 시 카메라 정리
  useEffect(() => {
    return () => { activeRef.current = false; stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopStream = () => {
    activeRef.current = false;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const startScan = async () => {
    setErrMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      setStep("scanning");
      await new Promise(r => setTimeout(r, 100));
      if (!videoRef.current) throw new Error("카메라 화면을 초기화할 수 없습니다");
      videoRef.current.srcObject = stream;
      await videoRef.current.play().catch(() => {});
      activeRef.current = true;
      tick();
    } catch (e) {
      stopStream();
      setErrMsg(
        e.name === "NotAllowedError"
          ? "카메라 권한을 허용해주세요.\n브라우저 주소창 왼쪽 자물쇠 아이콘 → 카메라 허용"
          : "카메라 오류: " + e.message
      );
      setStep("error");
    }
  };

  const tick = () => {
    if (!activeRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState < 2 || !video.videoWidth) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    let imageData;
    try { imageData = ctx.getImageData(0, 0, canvas.width, canvas.height); }
    catch { rafRef.current = requestAnimationFrame(tick); return; }
    const code = jsQR(imageData.data, canvas.width, canvas.height, { inversionAttempts: "attemptBoth" });
    if (code?.data) {
      activeRef.current = false;
      stopStream();
      handleScanned(code.data);
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const handleScanned = async (rawValue) => {
    setStep("processing");
    try {
      // 페이로드 = JSON { v:1, t:"passenger", tokenId, companyId, empNo }
      let payload;
      try { payload = JSON.parse(rawValue.trim()); }
      catch { throw new Error("승객 QR 형식이 아닙니다"); }
      if (payload?.t !== "passenger" || !payload?.tokenId) {
        throw new Error("승객 QR 형식이 아닙니다");
      }
      const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
      const r = await validateAndBoardByDriver({
        tokenId: payload.tokenId,
        driverId: driver?.id || "",
        companyId,
        routeId: dispatch?.routeId || "",
        routeName: dispatch?.routeName || "",
        vehicleId: dispatch?.vehicleId || driver?.vehicleId || "",
        vehicleNo: dispatch?.vehicleNo || driver?.vehicleNo || "",
        dispatchDate: today,
        stopId: currentStop?.id || "",
        stopName: currentStop?.name || "",
      });
      if ("vibrate" in navigator) navigator.vibrate([100, 50, 100]);
      setBoarded({ empNo: r.empNo, name: r.name });
      setStep("success");
      // 3초 후 자동 다음 스캔 모드 복귀(연속 탑승)
      setTimeout(() => { setBoarded(null); setStep("ready"); startScan(); }, 3000);
    } catch (e) {
      setErrMsg(e.message || String(e));
      setStep("error");
    }
  };

  const reset = () => {
    stopStream();
    setStep("ready"); setErrMsg(""); setBoarded(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, width: "100%" }}>
      {step === "ready" && (
        <>
          <div style={{ width: 90, height: 90, borderRadius: "50%", background: "var(--color-primary-soft)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-primary)" }}><Icon name="camera" size={40} stroke={1.6} /></div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "var(--color-label)", marginBottom: 4 }}>탑승 QR 스캔</div>
            <div style={{ fontSize: 12, color: "var(--color-label-mute)", lineHeight: 1.5 }}>
              직원의 본인 QR을 카메라로 비춰주세요
            </div>
          </div>
          <button onClick={startScan} style={{ ...S.primaryBtn, maxWidth: 280 }}>
            <Icon name="qr" size={18} /> 카메라 열기
          </button>
        </>
      )}

      {step === "scanning" && (
        <div style={{ width: "100%", maxWidth: 360 }}>
          <div style={{ position: "relative", borderRadius: 20, overflow: "hidden", background: "#000", aspectRatio: "1/1" }}>
            <video ref={videoRef} autoPlay playsInline muted
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            {/* 오버레이 — EmployeeApp ScanTab 패턴 동일 */}
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "18%", background: "rgba(0,0,0,.6)" }} />
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "18%", background: "rgba(0,0,0,.6)" }} />
              <div style={{ position: "absolute", top: "18%", left: 0, width: "10%", height: "64%", background: "rgba(0,0,0,.6)" }} />
              <div style={{ position: "absolute", top: "18%", right: 0, width: "10%", height: "64%", background: "rgba(0,0,0,.6)" }} />
              <div style={{ position: "absolute", top: "18%", left: "10%", width: 30, height: 30, borderTop: "3px solid var(--color-primary)", borderLeft: "3px solid var(--color-primary)", borderRadius: "6px 0 0 0" }} />
              <div style={{ position: "absolute", top: "18%", right: "10%", width: 30, height: 30, borderTop: "3px solid var(--color-primary)", borderRight: "3px solid var(--color-primary)", borderRadius: "0 6px 0 0" }} />
              <div style={{ position: "absolute", bottom: "18%", left: "10%", width: 30, height: 30, borderBottom: "3px solid var(--color-primary)", borderLeft: "3px solid var(--color-primary)", borderRadius: "0 0 0 6px" }} />
              <div style={{ position: "absolute", bottom: "18%", right: "10%", width: 30, height: 30, borderBottom: "3px solid var(--color-primary)", borderRight: "3px solid var(--color-primary)", borderRadius: "0 0 6px 0" }} />
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 12, fontSize: 13, color: "var(--color-primary)", fontWeight: 600 }}>
            직원의 QR을 사각형 안에 맞춰주세요
          </div>
          <button onClick={reset} style={{ ...S.refreshBtn, marginTop: 10 }}>취소</button>
        </div>
      )}

      {step === "processing" && (
        <>
          <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid var(--color-line)", borderTopColor: "var(--color-primary)", animation: "spin 0.8s linear infinite" }} />
          <div style={{ fontSize: 13, color: "var(--color-label-mute)" }}>탑승 처리 중...</div>
        </>
      )}

      {step === "success" && boarded && (
        <>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#E6F7EB", border: "2px solid var(--color-positive)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, color: "#007A29" }}>✓</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#007A29" }}>탑승 완료!</div>
          <div style={{ fontSize: 14, color: "var(--color-label)", fontWeight: 700 }}>
            {boarded.name} ({boarded.empNo})
          </div>
          <div style={{ fontSize: 11, color: "var(--color-label-alt)" }}>잠시 후 다음 스캔을 시작합니다…</div>
        </>
      )}

      {step === "error" && (
        <>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--color-atomic-red-90)", border: "2px solid var(--color-destructive)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, color: "var(--color-destructive)" }}>✕</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "var(--color-destructive)" }}>오류</div>
          <div style={{ fontSize: 13, color: "var(--color-label-mute)", textAlign: "center", whiteSpace: "pre-line", lineHeight: 1.6 }}>{errMsg}</div>
          <button onClick={reset} style={{ ...S.primaryBtn, maxWidth: 280 }}>다시 시도</button>
        </>
      )}
    </div>
  );
}

// ─── 길안내 (2026-07-28 way 요청 "초보 기사를 위한 네비게이션") ─────────────
// 기사앱에는 지도가 **아예 없었다**(승객·직원·관리자 앱엔 전부 있는데) → 초행 노선을 맡은
// 기사가 다음에 어디로 가야 하는지 앱에서 확인할 방법이 없었다. 이 탭이 그 구멍을 메운다.
//
// ⚠ 웹은 턴바이턴 음성 안내를 못 한다 → 음성이 필요하면 외부 내비(카카오맵)로 넘긴다.
//   그런데 외부 앱을 띄우면 이 앱이 백그라운드로 가 위치 전송이 끊긴다(2026-06-26 실측:
//   앱이 화면 앞에 있었는데도 GPS 가 65분 비어 관제에 버스가 멈춰 보였다).
//   → 폰 GPS 차량에는 경고를 같이 띄운다. 단말(device) 차량은 위치가 단말에서 오므로 무관.
//   2026-10-21: 스토어 앱(BusLink 운행) 안에서는 끊기지 않으므로 경고 없음(아래 카카오맵 버튼).
//
// 판정은 순수 모듈 `lib/navGuide.js`(격리 테스트), 여기선 표시만. gps.js·stopSchedule.js
// 핫경로는 건드리지 않는다(회귀 가드 다수).

/** 운행 중 기본 확대 단계 — 실제 내비처럼 **가까이**(level 3 ≈ 1m/px, 화면에 약 400~500m).
 *  2026-07-31 way: "네비 볼 때도 큰 축척으로 길 찾기 좋게 보잖아". 기사가 ＋/－ 로 조정 가능. */
const NAV_ZOOM_DEFAULT = 3;
const NAVI_RETRY_MS = [5000, 15000, 30000]; // 도로 경로 실패 시 자동 재시도 간격(2026-10-23)
/** 이만큼 가까워지면 정류장 안내문을 띄운다(그 전엔 지도를 가리지 않는 게 낫다). */
const NAV_NEAR_M = 300;
/** 회전까지 이만큼 남으면 대형 픽토그램을 진하게·크게(그 전엔 옅게 떠 있어 지도를 안 가린다).
 *  200m ≈ 시속 40km 로 18초 — 차로를 바꾸기 시작해야 하는 거리(2026-08-06). */
const NAV_TURN_NEAR_M = 200;
/** 지도 위에 얹는 정보 칩 — 반투명 흰 배경으로 지도를 완전히 가리지 않는다. */
const navChip = {
  background: "rgba(255,255,255,.93)", borderRadius: 10, padding: "7px 11px",
  boxShadow: "0 2px 10px rgba(0,0,0,.16)", color: "var(--color-label)", fontSize: 13,
};

function DriverNavGuide({ companyId, routeId, stops, routePath, currentStopIdx, livePos, estByStopId, deviceGps, compact = false }) {
  const [myPos, setMyPos] = useState(null);
  const [myGpsHeading, setMyGpsHeading] = useState(null); // navigator 가 준 진행 방향(정차 시 null)
  const [mySpeed, setMySpeed] = useState(null);           // 내 속도 m/s (정차·미지원이면 null)
  const [center, setCenter] = useState(null);
  const [zoom, setZoom] = useState(NAV_ZOOM_DEFAULT);
  const [follow, setFollow] = useState(true);       // 내 위치 자동 추적(기사가 지도를 만지면 해제)
  const [headingUp, setHeadingUp] = useState(true); // 진행 방향이 화면 위(2026-07-31 way 요청)
  const mapObjRef = useRef(null);
  const clipRef = useRef(null);
  const stageRef = useRef(null);
  const [clipBox, setClipBox] = useState({ w: 0, h: 0 });
  // 카카오모밀리티 도로 경로·회전 안내(2026-07-29). 저장하지 않고 화면에서만 쓴다.
  const [navi, setNavi] = useState(null);        // {path, guides}
  const [naviState, setNaviState] = useState("idle"); // idle|loading|retry|ok|fail

  // 🔴 실패하면 5·15·30초 뒤 저절로 다시 받는다(2026-10-23 way 아침 테스트 «방향지시가 안 나온다»).
  //    그날 06:55~07:14 서버가 요청을 문 앞에서 500 으로 거부했다(Cloud Run 기동 실패 추정 · 지금은 정상).
  //    예전엔 1회 실패로 «다시 시도» 버튼만 남아 운전 중인 기사는 끝까지 회전 안내 없이 달렸다.
  //    seq 로 노선이 바뀐 뒤 늦게 온 응답·예약된 재시도를 버린다. 버튼은 0회차부터 새로 시작.
  const naviRetryRef = useRef({ timer: null, seq: 0 });
  const loadNavi = useCallback(async (attempt = 0) => {
    if (!companyId || !routeId) return;
    const st = naviRetryRef.current;
    clearTimeout(st.timer); st.timer = null;
    const n = typeof attempt === "number" ? attempt : 0;   // onClick 이벤트가 들어와도 0회차
    if (n === 0) st.seq++;
    const seq = st.seq;
    setNaviState("loading");
    let d = null;
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "getNaviRoute");
      const r = await fn({ companyId, routeId });
      d = r?.data || {};
    } catch (e) {
      console.warn(`[BusLink] 길찾기 로드 실패(${n + 1}회차):`, e?.message || e);
    }
    if (seq !== st.seq) return;                            // 그사이 노선이 바뀌었거나 다시 시작했다
    if (d && Array.isArray(d.path) && d.path.length >= 2) {
      setNavi({ path: d.path, guides: Array.isArray(d.guides) ? d.guides : [], matchedToDrawn: !!d.matchedToDrawn,
                speeds: Array.isArray(d.speeds) ? d.speeds : [], states: Array.isArray(d.states) ? d.states : [] });
      setNaviState("ok");
      return;
    }
    if (n < NAVI_RETRY_MS.length) {
      setNaviState("retry");
      st.timer = setTimeout(() => { if (seq === naviRetryRef.current.seq) loadNavi(n + 1); }, NAVI_RETRY_MS[n]);
    } else setNaviState("fail");
  }, [companyId, routeId]);
  useEffect(() => () => { const st = naviRetryRef.current; clearTimeout(st.timer); st.seq++; }, []);

  // ── 경로 이탈 재탐색(2026-10-22 way 실주행 "경로에서 벗어난 길로 가면 안내가 끊긴다") ──
  // 노선 경로에서 벗어나 일정 시간 지나면 **지금 위치 → 다음 정류장** 길을 다시 받는다.
  // 노선 경로에 다시 올라서면 재탐색 경로를 버리고 원래 안내로 돌아간다. 판정은 순수 함수
  // `rerouteDecision`(격리 테스트). 결과는 저장하지 않는다(카카오 정책).
  const [detour, setDetour] = useState(null);           // {path, guides, speeds, states, toStopId}
  const [detourState, setDetourState] = useState("idle"); // idle|loading|fail
  const offSinceRef = useRef(null);
  const lastRerouteAtRef = useRef(0);
  const rerouteSeqRef = useRef(0);
  const requestDetour = useCallback(async (from, toStopId) => {
    if (!companyId || !routeId || !from || !toStopId) return;
    const seq = ++rerouteSeqRef.current;
    lastRerouteAtRef.current = Date.now();
    setDetourState("loading");
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "getNaviRoute");
      const r = await fn({ companyId, routeId, from: { lat: from.lat, lng: from.lng }, toStopId });
      if (seq !== rerouteSeqRef.current) return;        // 그사이 노선·정류장이 바뀌었다
      const d = r?.data || {};
      if (Array.isArray(d.path) && d.path.length >= 2) {
        setDetour({ path: d.path, guides: Array.isArray(d.guides) ? d.guides : [], toStopId,
                    speeds: Array.isArray(d.speeds) ? d.speeds : [], states: Array.isArray(d.states) ? d.states : [] });
        setDetourState("idle");
      } else setDetourState("fail");
    } catch (e) {
      if (seq !== rerouteSeqRef.current) return;
      console.warn("[BusLink] 경로 재탐색 실패:", e?.message || e);
      setDetourState("fail");
    }
  }, [companyId, routeId]);

  // 노선이 정해지면 1회 로드. 결과는 저장하지 않으므로 노선이 바뀌면 다시 받는다.
  useEffect(() => {
    setNavi(null); setDetour(null); setDetourState("idle");
    rerouteSeqRef.current++; offSinceRef.current = null;
    loadNavi();
  }, [loadNavi]);

  // 운행 시작 전에도 길안내가 필요하다(초행 노선 미리보기) → 이 탭이 열려 있는 동안만
  // 자체 측위. startGPS 파이프라인은 건드리지 않고 별도 watch 를 쓰고 언마운트 시 해제.
  useEffect(() => {
    if (!navigator.geolocation) return;
    const w = navigator.geolocation.watchPosition(
      p => {
        setMyPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        if (typeof p.coords.heading === "number" && !isNaN(p.coords.heading)) setMyGpsHeading(p.coords.heading);
        // 내 속도 — 폰이 주는 m/s. 정차·미지원이면 null 이라 화면에서 감춘다(0 으로 지어내지 않는다).
        setMySpeed(typeof p.coords.speed === "number" && !isNaN(p.coords.speed) ? p.coords.speed : null);
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 }
    );
    return () => navigator.geolocation.clearWatch(w);
  }, []);

  // ── 내 위치 (2026-08-06 way "차량 위치가 뚝뚝 끊어져서 이동") ──────────────
  // 🔴 예전엔 `livePos || myPos` 였다 — 운행 중에는 **서버에 올라간 위치**를 썼는데,
  //   그건 `startGPS` 가 **5m/5초 스로틀**로 올린 것을 Firestore 왕복해 받은 값이라
  //   기사 화면에서 5초마다 뚝 끊겨 튄다. 정작 **내 폰의 GPS 가 훨씬 촘촘한데** 안 쓰고 있었다.
  //   → 내 폰 위치를 1순위로. 폰 측위가 없을 때만(권한 거부·device 차량) 서버 위치 폴백.
  //   ⚠ 이건 **기사 본인 화면**의 이야기다. 관제·승객앱이 보는 값은 그대로 서버 위치다.
  const rawPos = myPos || livePos;

  // 승객·관제 지도와 같은 rAF 보간을 기사 화면에도 적용한다(여긴 빠져 있었다).
  // 판정(회전 안내까지 남은 거리·정류장 도착)은 **원본 좌표**로, 보간값은 **그리기 전용**.
  const posList = useMemo(
    () => (rawPos ? [{ id: "me", lat: rawPos.lat, lng: rawPos.lng }] : []),
    [rawPos && rawPos.lat, rawPos && rawPos.lng] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const smoothed = useAnimatedPositions(posList);
  const pos = rawPos;                                  // 판정용(거리·안내 시점)
  const drawPos = smoothed[0] || rawPos;               // 그리기용(마커·지도 중심·경로 분할)

  // 진행 방향 — 지도를 돌릴 수 없으니 마커를 돌린다. 직전 위치를 기억해 이동 벡터로 산출.
  const prevPosRef = useRef(null);
  const headingRef = useRef(null);
  const [heading, setHeading] = useState(null);
  useEffect(() => {
    if (!pos) return;
    const h = travelHeading({
      prev: prevPosRef.current, cur: pos,
      gpsHeading: myGpsHeading, fallback: headingRef.current,
    });
    if (h !== null && h !== headingRef.current) { headingRef.current = h; setHeading(h); }
    // 충분히 움직였을 때만 기준점을 옮긴다(정차 중 미세 흔들림으로 방향이 튀지 않게).
    if (!prevPosRef.current || haversine(prevPosRef.current, pos) >= 8) prevPosRef.current = pos;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos && pos.lat, pos && pos.lng]);
  // 카카오 로고·축척 표시는 지도 컨테이너의 **직계 자식**이라(실측) 판을 통째로 돌리면
  // 같이 기울고 모서리 밖으로 밀려난다 → 회전하지 않는 바깥 상자로 **원래 요소 그대로**
  // 옮겨 세워 둔다. 지우거나 가리는 건 카카오 이용약관 위반이다.
  const liftCredits = useCallback(() => {
    const clip = clipRef.current, stage = stageRef.current;
    if (!clip || !stage) return;
    const node = stage.firstElementChild;
    if (!node) return;
    const link = node.querySelector('a[href*="map.kakao.com"]');
    if (!link) return;                       // 이미 옮겼거나 아직 안 붙었다
    let box = link;
    while (box.parentElement && box.parentElement !== node) box = box.parentElement;
    if (box.parentElement !== node) return;
    box.style.position = "absolute";
    box.style.left = "6px";
    box.style.right = "auto";
    box.style.top = "auto";
    box.style.bottom = "4px";
    box.style.zIndex = "3";
    clip.appendChild(box);
  }, []);

  // 지도 상자 실측 — 회전해도 모서리가 비지 않으려면 판이 **대각선 길이**만큼 커야 한다.
  useEffect(() => {
    const el = clipRef.current;
    if (!el) return;
    const upd = () => {
      const r = el.getBoundingClientRect();
      setClipBox({ w: Math.round(r.width), h: Math.round(r.height) });
    };
    upd();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 지도 중심 모드 높이 — 화면 아래 끝까지 채운다(2026-08-05 way "최대한 지도 중심으로").
  // 🔴 고정 px 을 빼는 방식(예 `calc(100dvh - 128px)`)은 기기·글꼴·안전영역에 따라 어긋난다
  //   → 지도 상자의 실제 위치를 재서 남은 높이를 그대로 준다.
  //   상자 top 은 위쪽 내용만으로 정해지고 자기 높이와 무관하므로 되먹임 루프가 없다.
  const [compactH, setCompactH] = useState(0);
  useEffect(() => {
    if (!compact) { setCompactH(0); return undefined; }
    const measure = () => {
      const el = clipRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setCompactH(Math.max(360, Math.round(window.innerHeight - top - 12)));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [compact]);

  const guide = computeGuide({ stops, currentStopIdx, pos, estByStopId });
  const target = guide.stop;
  const targetLL = target ? stopLatLng(target) : null;
  const naviUrl = target ? kakaoMapDirectionsUrl(target) : null;

  // 경로 우선순위: ① 카카오 도로 경로(회전 안내 포함) ② 관리자가 그린 routePath
  // ③ 정류장 직선. ①이 실패해도 ②③으로 자동 폴백되므로 화면이 비지 않는다.
  const drawn = toLatLngPath(routePath);
  const basePath = (navi && navi.path.length >= 2)
    ? navi.path
    : drawn.length >= 2 ? drawn : stops.map(stopLatLng).filter(Boolean);
  // 재탐색 경로는 **지금 안내 중인 정류장**으로 가는 것일 때만 쓴다(그 정류장을 지나면 버린다).
  const detourActive = !!(detour && target && detour.toStopId === target.id && detour.path.length >= 2);
  const path = detourActive ? detour.path : basePath;
  // 🔴 판정·회전 안내는 **다음 정류장까지** 구간으로(2026-10-22 실측) — 노선 전체로 재면
  //   노선 뒤쪽(종점 근처 차고지·사무실)에 있을 때 "경로 위"가 되어 재탐색이 안 걸리고
  //   1번 정류장이 11km 밖인데 "20m 앞 목적지"가 떴다.
  const baseToTarget = pathToTarget({ path: basePath, targetLL });
  const guidePath = detourActive ? detour.path : baseToTarget;
  const onBase = onPathWithin(pos, baseToTarget);
  const onDetour = detourActive && onPathWithin(pos, detour.path);

  // 서 있으면 GPS 좌표가 안 바뀌어 아래 판정이 다시 안 돈다(차고지 대기 등) → 노선 밖에 있는
  // 동안만 2초마다 다시 본다. 노선 위면 타이머 없음.
  const [rerouteTick, setRerouteTick] = useState(0);
  const offRouteNow = !!(pos && target && target.id && !onBase && !onDetour);
  useEffect(() => {
    if (!offRouteNow) return undefined;
    const t = setInterval(() => setRerouteTick(n => n + 1), 2000);
    return () => clearInterval(t);
  }, [offRouteNow]);

  // 이탈 판정 — GPS 가 들어올 때마다(원본 좌표 기준). 정류장이 없거나 위치를 모르면 판정하지 않는다.
  useEffect(() => {
    if (!pos || !target || !target.id || basePath.length < 2) { offSinceRef.current = null; return; }
    const d = rerouteDecision({
      onBase, onDetour, offSince: offSinceRef.current, now: Date.now(),
      lastRequestAt: lastRerouteAtRef.current, loading: detourState === "loading",
    });
    offSinceRef.current = d.offSince;
    if (d.action === "clear" && detour) { rerouteSeqRef.current++; setDetour(null); setDetourState("idle"); }
    else if (d.action === "request") requestDetour(pos, target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos && pos.lat, pos && pos.lng, target && target.id, onBase, onDetour, rerouteTick]);

  const activeGuides = detourActive ? detour.guides : (navi ? navi.guides : null);
  const activeTraffic = detourActive ? detour : navi;
  // 회전 안내는 카카오 경로가 있을 때만(손으로 그린 경로로는 회전을 못 잡는다 — 실측 확인).
  const turn = activeGuides ? nextNaviGuide({ guides: activeGuides, path: guidePath, pos }) : null;
  // 대형 픽토그램(2026-08-06 way "지도 위에 엄청 크게 · 애니메이션") — 배너와 **같은 문구**에서
  // 종류를 뽑는다. 모르는 안내는 null 이라 큰 그림도 안 그린다(틀린 그림보다 없는 게 낫다).
  const turnIcon = turn ? turnIconKind(turn.guide.guidance) : null;
  // 다 와 갈 때만 진하게·크게 — 평소엔 옅게 떠 있어 지도를 안 가린다.
  // ⚠ 남은 거리 필드는 `aheadMeters` 다(정류장 쪽 `remainMeters` 와 이름이 다르다 — 섞으면
  //   항상 undefined 라 "가까움"이 영영 안 켜지고, 에러도 안 난다).
  const turnNear = !!turn && Number.isFinite(turn.aheadMeters) && turn.aheadMeters <= NAV_TURN_NEAR_M;

  // ── 속도 표시(2026-08-06 way 요청) ─────────────────────────────────────────
  //   ① 내 속도 = 폰 GPS(coords.speed). 정차·미지원이면 null → 감춘다.
  //   ② 지금 달리는 구간의 교통 속도·정체 단계 = 카카오가 도로마다 주는 traffic_speed/state
  //      (CF 가 path 와 같은 길이의 speeds/states 로 내려준다) → 내 위치를 경로에 투영해
  //      얻은 segIndex 로 집는다. 경로 밖이거나 값이 없으면 표시하지 않는다.
  //   🔴 모르는 값을 0 으로 지어내지 않는다 — 운전 중 화면에서 0km/h 는 오해를 만든다.
  const myKmh = speedKmh(mySpeed);
  const roadTraffic = (() => {
    const tr = activeTraffic;
    if (!tr || !Array.isArray(tr.speeds) || !tr.speeds.length || !pos || path.length < 2) return null;
    const pr = projectToPolyline(pos, path, buildCumulativeLengths(path));
    if (!pr || pr.perpDist > 300) return null;          // 경로 밖이면 엉뚱한 구간을 집는다
    const i = Math.min(pr.segIndex, tr.speeds.length - 1);
    const kmh = tr.speeds[i];
    const lab = trafficLabel(tr.states && tr.states[i]);
    if (!Number.isFinite(kmh) && !lab) return null;
    return { kmh: Number.isFinite(kmh) ? kmh : null, label: lab };
  })();

  // 길안내의 핵심 — 노선 전체를 한 줄로 그리면 "지금 어디로 가라"가 안 읽힌다.
  // 지나온 / **지금 가야 할** / 그 이후로 잘라 가야 할 구간만 굵게 뽑는다.
  // 직전 정류장 — 경로에서 멀리 떨어져 있을 때(차고지·출발 전) 기준점이 된다.
  const prevLL = guide.targetIdx > 0 ? stopLatLng(stops[guide.targetIdx - 1]) : null;
  const seg = splitGuidePath({ path, pos: drawPos, targetLL, prevLL }); // 그리기 — 마커와 굵은 구간이 어긋나지 않게 보간값 사용

  const beforeRun = currentStopIdx < 0;

  // 지도 판 회전 — 진행 방향이 화면 위로 오게 한다(2026-07-31 way 결정).
  // ⚠ 카카오 지도 웹 API 에는 지도 회전 기능이 없다 → 지도 판을 CSS 로 돌리고,
  //   그 위의 표시물(정류장 번호·로고)은 반대로 돌려 **글자만 세워** 둔다.
  //   지도 타일에 인쇄된 도로명은 같이 돌아 남쪽 주행 시 거꾸로 보인다(감수한 대가).
  const rot = headingUp && heading != null ? -heading : 0;
  const rotating = rot !== 0;
  const counterRot = -rot;                               // 표시물을 세우는 반대 회전
  const stageSize = Math.ceil(Math.hypot(clipBox.w, clipBox.h)) || 0;

  // 판 크기·회전 모드가 바뀌면 카카오에 다시 재게 한다(안 하면 타일이 잘린 채로 남는다).
  useEffect(() => {
    const m = mapObjRef.current;
    if (!m) return;
    m.relayout();
    liftCredits();
  }, [stageSize, rotating, liftCredits]);

  // 시점 — 운행 전엔 **노선 전체**(기사가 먼저 알아야 할 건 "이 노선이 어디로 도는가"),
  // 운행 중엔 **크게 확대해서 내 위치를 따라간다**(2026-07-31 way: "네비처럼 큰 축척으로").
  // 자동 추적은 기사가 지도를 손으로 끌면 즉시 풀린다 → 사용자 조작과 싸우지 않는다
  // (2026-06-23 관제에서 같은 함정을 겪었다. 그때 배운 건 "매 갱신마다 강제 재센터 금지"이지
  //  "따라가지 말라"가 아니다 — 길안내에선 따라가되 손대면 놓는다).
  const fittedRef = useRef("");
  useEffect(() => {
    const key = `${routeId || ""}|${beforeRun ? "pre" : "run"}`;
    if (fittedRef.current === key) return;
    if (beforeRun) {
      if (path.length < 2) return;
      const v = guideView({ pos, targetLL, path });
      if (!v) return;
      setCenter(v.center); setZoom(v.level); setFollow(false);
    } else {
      if (!pos) return;
      setZoom(NAV_ZOOM_DEFAULT); setFollow(true);
    }
    fittedRef.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, beforeRun, path.length, pos, targetLL]);

  // 🔴 deps 는 **보간값(drawPos)** 이어야 한다 — 원본(pos)에 걸면 GPS 가 들어오는 순간에만
  //   재센터돼 지도가 5초에 한 번 뚝 끊겨 움직인다(마커만 부드럽고 지도는 그대로 튀는 상태).
  useEffect(() => {
    if (!follow || !drawPos) return;
    const c = navCenter({ pos: drawPos, heading, level: zoom, heightPx: clipBox.h || 420 });
    if (c) setCenter(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [follow, drawPos && drawPos.lat, drawPos && drawPos.lng, heading, zoom, clipBox.h]);

  // ── 지도를 손으로 만졌을 때 (2026-08-05 way 현장 지적 2건) ────────────────
  //   ① "지도 움직일 때 손가락 방향대로 안 움직임" — 판이 CSS 로 회전해 있으면 카카오는
  //      드래그를 **자기(판) 좌표계**로 해석하는데 손가락은 화면 좌표계로 움직여 각도만큼
  //      어긋난다. 회전 중에는 카카오 기본 드래그를 끄고 우리가 델타를 되돌려 panBy 한다.
  //   ② "움직이고 나서 제자리 안 돌아옴" — 추적을 놓기만 하고 복귀가 수동 버튼뿐이었다.
  //      손을 뗀 뒤 일정 시간 조작이 없으면 자동으로 다시 따라간다(내비 관례).
  const FOLLOW_RESUME_MS = 8000;
  const resumeTimerRef = useRef(null);
  const touchedMap = useCallback(() => {
    setFollow(false);
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => { resumeTimerRef.current = null; setFollow(true); }, FOLLOW_RESUME_MS);
  }, []);
  useEffect(() => () => { if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current); }, []);

  // 회전 중에는 카카오 기본 드래그·확대를 끄고(어긋난 방향으로 끌리므로) 우리가 처리한다.
  useEffect(() => {
    const m = mapObjRef.current;
    if (!m) return;
    if (typeof m.setDraggable === "function") m.setDraggable(!rotating);
    if (typeof m.setZoomable === "function") m.setZoomable(!rotating);
  }, [rotating]);

  // ── 회전 중 손가락 조작 (2026-10-22 way 실주행 «드래그도 확대·축소도 안 된다») ──
  // 예전엔 손가락 하나(panRef)만 따라가 두 손가락 확대가 아예 없었고, React 버블 단계로
  // 받아 카카오 지도가 이벤트를 먼저 삼키면 드래그도 안 먹었다.
  // 🔴 그래서 **캡처 단계**(onPointer*Capture)로 받는다 — 지도보다 먼저 우리에게 온다.
  // 🔴 손가락마다 따로 기억한다: 1개 = 이동(화면 델타 → 판 좌표계, rotateDragDelta),
  //    2개 = 확대·축소(간격 비율 → 레벨, pinchLevel) + 두 손가락 가운데점 이동.
  // 🔴 버튼(＋/－·내 위치·북쪽 고정) 위에서 시작한 손가락은 건드리지 않는다(누름이 그대로 간다).
  const ptrsRef = useRef(new window.Map());   // 카카오 SDK 의 Map 이 내장 Map 을 가린다(issues 2026-08-10)
  const pinchRef = useRef(null);
  const midOf = (ps) => {
    let x = 0, y = 0; ps.forEach(p => { x += p.x; y += p.y; });
    return { x: x / ps.length, y: y / ps.length };
  };
  const onStagePointerDown = (e) => {
    if (!rotating) return;                      // 북쪽 고정이면 카카오 기본 조작 그대로
    if (e.target && e.target.closest && e.target.closest("button")) return;
    ptrsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    touchedMap();
    const ps = [...ptrsRef.current.values()];
    if (ps.length === 2) {
      const m = mapObjRef.current;
      const lvl = m && typeof m.getLevel === "function" ? m.getLevel() : zoom;
      pinchRef.current = { startDist: Math.hypot(ps[0].x - ps[1].x, ps[0].y - ps[1].y), startLevel: lvl, mid: midOf(ps) };
    }
  };
  const onStagePointerMove = (e) => {
    const cur = ptrsRef.current;
    if (!cur.has(e.pointerId)) return;
    const prev = cur.get(e.pointerId);
    const m = mapObjRef.current;
    if (cur.size === 1) {
      cur.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (!m || typeof m.panBy !== "function") return;
      const d = rotateDragDelta({ dx: e.clientX - prev.x, dy: e.clientY - prev.y, rotDeg: rot });
      if (d.dx || d.dy) m.panBy(-d.dx, -d.dy);  // panBy 는 중심을 옮기므로 부호를 뒤집어야 내용이 손가락을 따라온다
      return;
    }
    cur.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pz = pinchRef.current;
    if (!pz || cur.size < 2) return;
    const ps = [...cur.values()].slice(0, 2);
    const dist = Math.hypot(ps[0].x - ps[1].x, ps[0].y - ps[1].y);
    const lvl = pinchLevel({ startLevel: pz.startLevel, startDist: pz.startDist, dist, min: 1, max: 10 });
    if (lvl != null && lvl !== zoom) setZoom(lvl);
    const mid = midOf(ps);
    if (m && typeof m.panBy === "function") {
      const d = rotateDragDelta({ dx: mid.x - pz.mid.x, dy: mid.y - pz.mid.y, rotDeg: rot });
      if (d.dx || d.dy) m.panBy(-d.dx, -d.dy);
    }
    pz.mid = mid;
  };
  const endStagePan = (e) => {
    ptrsRef.current.delete(e.pointerId);
    if (ptrsRef.current.size < 2) pinchRef.current = null;
  };

  const mapCenter = center || targetLL || drawPos || path[0] || { lat: 37.3894, lng: 126.9522 };
  const setZoomBy = (d) => {
    setZoom(z => Math.max(1, Math.min(10, (Number.isFinite(z) ? z : NAV_ZOOM_DEFAULT) + d)));
  };

  const stopPts = stops.map((s, i) => ({ s, i, ll: stopLatLng(s) })).filter(x => x.ll);
  // 정류장 설명은 **가까워졌을 때만** 띄운다 — 운전 중엔 지도가 가려지는 게 더 손해다.
  const nearTarget = Number.isFinite(guide.remainMeters) && guide.remainMeters <= NAV_NEAR_M;

  const ctlBtn = {
    fontSize: 12, fontWeight: 800, padding: "8px 11px", borderRadius: 9,
    border: "1px solid var(--color-line)", background: "rgba(255,255,255,.94)",
    color: "var(--color-label)", cursor: "pointer", fontFamily: "inherit",
    boxShadow: "0 2px 8px rgba(0,0,0,.18)", pointerEvents: "auto",
  };

  return (
    <div style={{ ...S.listCard, padding: 0, overflow: "hidden" }}>
      {/* 지도가 이 화면의 본체다 — 안내는 **지도 위에 얹는다**.
          예전엔 안내 카드(사진 포함)를 지도 위에 쌓아 지도가 320px 로 밀려 있었다
          (2026-07-31 기사 지적: "사진까지 보기 어렵고 정작 지도가 잘 안 보인다"). */}
      <div ref={clipRef}
        onPointerDownCapture={onStagePointerDown} onPointerMoveCapture={onStagePointerMove}
        onPointerUpCapture={endStagePan} onPointerCancelCapture={endStagePan}
        onPointerLeave={e => { if (e.pointerType === "mouse") endStagePan(e); }}
        style={{ position: "relative",
                 // 지도 중심 모드 = 실측한 남은 높이(위 compactH). 측정 전 첫 프레임만 폴백.
                 height: compact ? (compactH ? `${compactH}px` : "calc(100dvh - 128px)") : "64vh",
                 minHeight: compact ? 420 : 340,
                 overflow: "hidden", background: "#eef1f6",
                 touchAction: rotating ? "none" : "auto" }}>
        {/* 회전 판 — 진행 방향이 위로 오게 통째로 돌린다. 회전 시 모서리가 비지 않도록
            판을 상자의 **대각선 길이**만큼 키우고 중심을 맞춰 둔다. */}
        <div ref={stageRef} style={{
          position: "absolute", left: "50%", top: "50%",
          width: rotating && stageSize ? stageSize : "100%",
          height: rotating && stageSize ? stageSize : "100%",
          transform: `translate(-50%,-50%) rotate(${rot}deg)`,
          transformOrigin: "50% 50%",
          transition: "transform .5s ease-out",
        }}>
          <Map center={mapCenter} style={{ width: "100%", height: "100%" }} level={zoom}
            onCreate={m => {
              mapObjRef.current = m;
              m.relayout(); setTimeout(() => m.relayout(), 300);
              // 회전 중이면 처음부터 카카오 기본 조작을 끈다(위 effect 보다 지도가 늦게 생길 수 있다).
              if (typeof m.setDraggable === "function") m.setDraggable(!rotating);
              if (typeof m.setZoomable === "function") m.setZoomable(!rotating);
              const ev = window.kakao && window.kakao.maps && window.kakao.maps.event;
              if (ev) {
                // 기사가 지도를 끌면 자동 추적을 놓는다(사용자 조작과 싸우지 않는다).
                ev.addListener(m, "dragstart", () => touchedMap());
                ev.addListener(m, "zoom_changed", () => {
                  const l = m.getLevel();
                  if (Number.isFinite(l)) setZoom(l);
                });
              }
              liftCredits(); setTimeout(liftCredits, 400); setTimeout(liftCredits, 1500);
            }}>
            {/* 재탐색 중 — 원래 노선을 흐리게 깔아 둔다(어디서 다시 합류하는지 보이게) */}
            {detourActive && basePath.length >= 2 && (
              <Polyline path={basePath} strokeWeight={4} strokeColor="#8896AA" strokeOpacity={0.5} strokeStyle="shortdash" />
            )}
            {/* 그 이후 구간 — 흐리게(맥락만) */}
            {seg.later.length >= 2 && (
              <Polyline path={seg.later} strokeWeight={4} strokeColor="#0066FF" strokeOpacity={0.25} strokeStyle="shortdash" />
            )}
            {/* 지나온 구간 — 회색 */}
            {seg.passed.length >= 2 && (
              <Polyline path={seg.passed} strokeWeight={4} strokeColor="#8896AA" strokeOpacity={0.45} strokeStyle="solid" />
            )}
            {/* ★ 지금 가야 할 구간 — 굵고 진하게. 이 화면에서 눈이 제일 먼저 가야 할 것 */}
            {seg.ahead.length >= 2 && (
              <Polyline path={seg.ahead} strokeWeight={10} strokeColor="#0066FF" strokeOpacity={0.95} strokeStyle="solid" />
            )}
            {/* 경로가 아예 없을 때만 전체 노선 폴백 표시 */}
            {seg.ahead.length < 2 && seg.later.length < 2 && path.length >= 2 && (
              <Polyline path={path} strokeWeight={5} strokeColor="#0066FF" strokeOpacity={0.6} strokeStyle="solid" />
            )}
            {stopPts.map(({ s, i, ll }) => (
              <CustomOverlayMap key={s.id || i} position={ll} yAnchor={0.5}>
                {/* 판이 돌아도 번호는 세워 둔다(반대로 되돌리는 회전) */}
                <div style={{
                  minWidth: 22, height: 22, padding: "0 5px", borderRadius: 11,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 900, fontFamily: "inherit",
                  background: i === guide.targetIdx ? "var(--color-primary)" : i <= currentStopIdx ? "var(--color-positive)" : "#fff",
                  color: i === guide.targetIdx || i <= currentStopIdx ? "#fff" : "var(--color-label)",
                  border: `2px solid ${i === guide.targetIdx ? "var(--color-primary)" : i <= currentStopIdx ? "var(--color-positive)" : "var(--color-line)"}`,
                  boxShadow: i === guide.targetIdx ? "0 0 0 5px rgba(0,102,255,0.22)" : "0 1px 4px rgba(0,0,0,.2)",
                  whiteSpace: "nowrap",
                  transform: counterRot ? `rotate(${counterRot}deg)` : "none",
                }}>
                  {i + 1}
                </div>
              </CustomOverlayMap>
            ))}
            {/* ★ 회전 지점 표시 — 2026-08-07 way 실주행 지적 3건을 한 번에 푼다.
                  "화살표가 너무 가운데라 경로를 가린다 · 화살표 있는 지점에 나타날 수 없나"
                  "방향전환까지 남은 m 를 같이"  "직진이 긴데도 계속 방향표시가 나온다"
                🔴 지도 한가운데 고정이 아니라 **실제 회전 지점 좌표**에 얹는다 — 그러면
                   ① 경로 위를 안 가리고 ② "저기서 꺾는다"가 위치로 읽히고
                   ③ 멀면 화면 밖이라 저절로 사라진다(긴 직진에 계속 뜨던 문제).
                🔴 판이 돌아도 패널은 세운다(정류장 번호와 같은 counterRot).
                🔴 애니메이션은 **패널이 아니라 뒤 고리**에 건다 — 패널엔 인라인 rotate 가
                   걸려 있어 키프레임이 transform 을 건드리면 그 선언이 조용히 사라진다. */}
            {turn && turnIcon && Number.isFinite(turn.guide.lat) && Number.isFinite(turn.guide.lng) && (
              <CustomOverlayMap position={{ lat: turn.guide.lat, lng: turn.guide.lng }} yAnchor={1} zIndex={4}>
                <div style={{ position: "relative", transform: counterRot ? `rotate(${counterRot}deg)` : "none" }}>
                  {/* 다 와 갈 때만 조용히 번지는 고리 — 흔들지 않는다 */}
                  {turnNear && (
                    <span style={{
                      position: "absolute", left: "50%", top: "50%", width: 92, height: 92,
                      marginLeft: -46, marginTop: -46, borderRadius: "50%",
                      border: "2px solid var(--color-primary)",
                      animation: "navturnring 2s ease-out infinite", pointerEvents: "none",
                    }} />
                  )}
                  <div style={{
                    position: "relative",
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 11px 7px 8px", borderRadius: 13,
                    background: turnNear ? "var(--color-primary)" : "rgba(23,34,54,.93)",
                    color: "#fff", whiteSpace: "nowrap",
                    boxShadow: "0 3px 12px rgba(0,0,0,.32), 0 0 0 2px rgba(255,255,255,.9)",
                    transition: "background .3s ease",
                  }}>
                    <NavTurnIcon kind={turnIcon.kind} size={34} title={turn.label} />
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
                      <span style={{ fontSize: 19, fontWeight: 900, letterSpacing: "-0.02em" }}>
                        {formatDistance(turn.aheadMeters)}
                      </span>
                      {turn.guide.name && (
                        <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.8, maxWidth: 108,
                          overflow: "hidden", textOverflow: "ellipsis" }}>
                          {turn.guide.name}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* 아래로 뻗는 꼬리 — 패널이 가리키는 지점을 명확히 */}
                  <span style={{
                    position: "absolute", left: "50%", bottom: -6, marginLeft: -6,
                    width: 0, height: 0, borderLeft: "6px solid transparent",
                    borderRight: "6px solid transparent",
                    borderTop: `7px solid ${turnNear ? "var(--color-primary)" : "rgba(23,34,54,.93)"}`,
                  }} />
                </div>
              </CustomOverlayMap>
            )}

            {/* 내 차량 — 화살표는 **화면 기준 진행 방향**을 가리킨다.
                판이 -heading 만큼 돌아 있으므로 여기에 heading 을 주면 화면에선 정확히
                위(=진행 방향)를 향한다. 북쪽 고정 모드(rot=0)에서는 실제 방위를 향한다.
                방향을 아직 못 잡았으면 원형 점 — 없는 방향을 그리면 거짓말이 된다. */}
            {drawPos && (
              <CustomOverlayMap position={drawPos} yAnchor={0.5} zIndex={5}>
                <div style={{
                  width: 38, height: 38, borderRadius: "50%",
                  background: "var(--color-primary)", border: "3px solid #fff",
                  boxShadow: "0 2px 10px rgba(0,0,0,.35)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 20, fontWeight: 900, lineHeight: 1,
                  transform: heading == null ? "none" : `rotate(${heading}deg)`,
                  transition: "transform .4s ease",
                }}>
                  {heading == null ? "●" : "▲"}
                </div>
              </CustomOverlayMap>
            )}
          </Map>
        </div>

        {/* ── 지도 위 안내 ── 지도를 가리지 않게 위쪽에만, 최소한으로.
            pointerEvents:none 이라 배너 위로도 지도를 끌 수 있다(버튼만 예외). */}
        <div style={{ position: "absolute", left: 0, right: 0, top: 0, padding: 10, zIndex: 4, pointerEvents: "none" }}>
          {/* ★ 회전 안내 — 운전 중엔 이게 제일 먼저 읽혀야 한다(거리 → 동작 순서) */}
          {turn && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "11px 13px", borderRadius: 12, marginBottom: 6,
              background: "var(--color-primary)", color: "#fff",
              boxShadow: "0 4px 14px rgba(0,0,0,.28)",
            }}>
              {/* 🔴 화살표는 배너 문구와 **같은 문자열**에서 뽑는다 — 예전엔 `↱` 하드코딩이라
                  좌회전 안내에도 우회전 화살표가 떴다(2026-08-05 way 현장 지적).
                  방향을 못 정하는 안내(지하차도 옆길 등)는 null → 화살표를 안 그린다. */}
              {turnIcon && (
                <NavTurnIcon kind={turnIcon.kind} glyph={turnGlyph(turn.guide.guidance)} size={30} title={turn.label} />
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                {/* 한국어는 keep-all 이라야 낱말 중간에서 끊기지 않는다(글자가 잘려 보이는 원인) */}
                <div style={{ fontSize: 21, fontWeight: 900, lineHeight: 1.25, wordBreak: "keep-all" }}>{turn.label}</div>
                {turn.guide.name && (
                  <div style={{ fontSize: 12, opacity: 0.9, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {turn.guide.name}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 다음 정류장 — 한 줄로 압축(이름·남은거리·예정시각) */}
          {guide.finished ? (
            <div style={{ ...navChip, fontWeight: 800 }}>오늘 운행 정류장을 모두 지났습니다</div>
          ) : !target ? (
            <div style={navChip}>이 노선에 등록된 정류장이 없습니다</div>
          ) : (
            <div style={{ ...navChip, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: "var(--color-primary-deep)" }}>
                {guide.isLast ? "종점" : `${guide.targetIdx + 1}번째`}
              </span>
              <span style={{ fontSize: 16, fontWeight: 900, color: "var(--color-label)" }}>
                {target.name || "이름 없음"}
              </span>
              <span style={{ fontSize: 16, fontWeight: 900, color: "var(--color-primary)" }}>
                {guide.remainLabel}
              </span>
              {guide.plannedAt && (
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--color-label-mute)" }}>
                  예정 {guide.plannedAt}
                </span>
              )}
            </div>
          )}

          {/* 속도 · 구간 교통 — 🔴 2026-08-07 way "속도표시가 없어졌음".
              2026-08-06 에 "메뉴를 가리지 않게" 지도 **밖** 줄로 내렸는데, 지도 중심 모드는
              지도가 `compactH`(화면 끝까지)라 그 줄이 통째로 **화면 밖**으로 밀렸다.
              게다가 지도 아래쪽은 고정 하단 바(운행 중 — 종료)가 덮는다.
              → 아무도 안 가리면서 항상 보이는 자리 = **상단 스택**(배너·정류장 칩과 같은 흐름).
              🔴 지도 위 떠 있는 칩(`position:absolute` + bottom)으로 되돌리지 말 것 —
                 그 띠는 카카오 로고·시점 버튼·하단 바가 이미 나눠 쓰고 있다. */}
          {(myKmh !== null || roadTraffic) && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
              {myKmh !== null && (
                <div style={{ ...navChip, display: "flex", alignItems: "baseline", gap: 3, padding: "5px 10px" }}>
                  <span style={{ fontSize: 18, fontWeight: 900, color: "var(--color-label)", lineHeight: 1 }}>{myKmh}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-label-mute)" }}>km/h</span>
                </div>
              )}
              {roadTraffic && (
                <div style={{ ...navChip, padding: "6px 10px", fontSize: 11, fontWeight: 800,
                  color: roadTraffic.label ? `var(--color-${roadTraffic.label.tone})` : "var(--color-label-mute)" }}>
                  이 구간 {roadTraffic.kmh !== null ? `${roadTraffic.kmh}km/h` : ""}{roadTraffic.label ? ` ${roadTraffic.label.text}` : ""}
                </div>
              )}
            </div>
          )}

          {/* 경로 이탈 재탐색 상태 — 🔴 지도 위에 띄운다(지도 아래 줄은 지도 중심 모드에서 화면 밖이다).
              안내가 그냥 바뀌거나 사라지면 기사는 고장으로 오해한다. */}
          {pos && target && !onBase && (detourActive || detourState !== "idle") && (
            <div style={{ ...navChip, marginTop: 6, fontSize: 12, fontWeight: 800,
              color: detourState === "fail" && !detourActive ? "var(--color-cautionary)" : "var(--color-primary-deep)" }}>
              {detourState === "loading" ? "경로를 벗어났습니다 · 새 경로를 찾는 중…"
                : detourActive ? `경로를 벗어나 ${target.name || "다음 정류장"}까지 새 경로로 안내 중`
                : "새 경로를 받지 못했습니다 · 잠시 후 다시 찾습니다"}
            </div>
          )}

          {/* 정류장 안내문은 **다 와 갈 때만** — 그때가 "어디에 세우나"가 필요한 순간이다 */}
          {target && nearTarget && target.description && (
            <div style={{ ...navChip, marginTop: 6, fontSize: 13, whiteSpace: "pre-wrap" }}>
              {target.description}
            </div>
          )}
          {!guide.hasPos && (
            <div style={{ ...navChip, marginTop: 6, fontSize: 12, fontWeight: 700, color: "var(--color-cautionary)" }}>
              위치를 확인하는 중입니다
            </div>
          )}
        </div>

        {/* 확대/축소 + 회전 전환 — 오른쪽 가운데(운전 중 한 손 조작).
            🔴 회전 전환은 예전에 **오른쪽 위**에 있었는데 그 자리가 회전 안내 배너와 겹쳐
               배너 글씨를 가렸다(2026-08-05 way "방향안내 글씨가 잘려서 보임").
               배너는 가로 전체를 써야 문구가 안 잘리므로, 지도 위쪽에는 아무 버튼도 두지 않는다. */}
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", zIndex: 5, display: "flex", flexDirection: "column", gap: 6, pointerEvents: "none" }}>
          <button onClick={() => setHeadingUp(v => !v)}
            style={{ ...ctlBtn, padding: "7px 9px", background: headingUp ? "var(--color-primary)" : "rgba(255,255,255,.94)", color: headingUp ? "#fff" : "var(--color-label)", border: headingUp ? "none" : ctlBtn.border }}>
            {headingUp ? "↑ 진행" : "N 북쪽"}
          </button>
          <button onClick={() => setZoomBy(-1)} style={{ ...ctlBtn, fontSize: 18, padding: "6px 12px" }}>＋</button>
          <button onClick={() => setZoomBy(1)} style={{ ...ctlBtn, fontSize: 18, padding: "6px 12px" }}>－</button>
        </div>

        {/* 시점 버튼 — 오른쪽 아래(왼쪽 아래는 카카오 로고 자리라 비워 둔다) */}
        <div style={{ position: "absolute", right: 10, bottom: 10, display: "flex", gap: 6, zIndex: 5, pointerEvents: "none" }}>
          {pos && !follow && (
            <button onClick={() => {
                if (resumeTimerRef.current) { clearTimeout(resumeTimerRef.current); resumeTimerRef.current = null; }
                setFollow(true); setZoom(NAV_ZOOM_DEFAULT);
              }}
              style={{ ...ctlBtn, background: "var(--color-primary)", color: "#fff", border: "none" }}>
              내 위치
            </button>
          )}
          {basePath.length >= 2 && (
            <button onClick={() => {
              setFollow(false);
              const v = guideView({ pos, targetLL, path: basePath });
              if (v) { setCenter(v.center); setZoom(v.level); }
            }} style={ctlBtn}>
              노선 전체
            </button>
          )}
        </div>
      </div>

      {/* 지도 아래 한 줄 — 경로 출처 + 외부 내비.
          ⚠ **지도 중심 모드에서는 이 줄이 화면 밖이다**(지도가 `compactH` 로 화면 끝까지 찬다).
             그래서 운전 중 반드시 보여야 하는 것(속도 등)을 여기 두면 안 된다 —
             2026-08-06 에 속도를 여기 내렸다가 way 가 "속도표시가 없어졌음" 으로 신고했다.
             운행 중 필요한 표시는 상단 스택(배너 아래)에 둔다. */}
      <div style={{ padding: "8px 12px 12px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, color: "var(--color-label-mute)", flex: 1, minWidth: 150, lineHeight: 1.45 }}>
          {/* 노선에서 멀리 떨어져 있으면 회전 안내를 끄고 그 사실을 말해준다 —
              안내가 그냥 없으면 기사는 고장으로 오해한다. */}
          {detourActive ? "경로를 벗어나 다음 정류장까지 새 경로로 안내 중 · 노선에 돌아오면 원래 안내로 돌아갑니다"
            : pos && !seg.onRoute ? (detourState === "loading" ? "경로를 벗어났습니다 · 새 경로를 찾는 중…"
              : "노선에서 떨어져 있어 회전 안내는 표시하지 않습니다 · 잠시 뒤 새 경로를 찾습니다")
            : naviState === "loading" ? "도로 경로를 불러오는 중…"
            : naviState === "ok" ? (navi && navi.matchedToDrawn
              ? "등록된 노선을 따라가는 도로 경로 · 회전 안내 사용 중"
              : "도로 경로 · 회전 안내 사용 중")
            : naviState === "retry" ? "등록된 노선 경로로 안내 중 · 도로 경로를 곧 다시 받습니다"
            : naviState === "fail" ? "등록된 노선 경로로 안내 중(도로 경로를 못 받았습니다)"
            : ""}
        </div>
        {(naviState === "fail" || naviState === "retry") && (
          <button onClick={() => loadNavi(0)}
            style={{ fontSize: 11, fontWeight: 700, color: "var(--color-primary-deep)", background: "none", border: "1px solid var(--color-line)", borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit" }}>
            다시 시도
          </button>
        )}
        {/* 🔴 스토어 앱(BusLink 운행) 안에서는 경고 없음(2026-10-21 way) — 앱은 상주 위치 서비스 +
            뒤로 가도 WebView 를 깨워 두는 처리(MainActivity)로 외부 앱을 띄워도 전송이 이어진다
            (폴드7 실측: 화면 끈 5분 분당 6점). 경고는 브라우저 기사에게만(거기선 여전히 끊긴다). */}
        {naviUrl && (() => {
          const gpsKept = deviceGps || isNativeApp();
          return (
            <a href={naviUrl} target="_blank" rel="noreferrer"
              title={gpsKept ? "" : "외부 내비를 켜면 이 앱이 뒤로 가면서 위치 전송이 멈출 수 있습니다"}
              style={{ fontSize: 12, fontWeight: 800, color: "var(--color-primary-deep)", background: "var(--color-primary-soft)", border: "none", borderRadius: 8, padding: "7px 11px", textDecoration: "none", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              카카오맵{gpsKept ? "" : " ⚠"}
            </a>
          );
        })()}
      </div>
    </div>
  );
}

// ─── NFC 사원증 태깅 (2026-07-22) ─────────────────────────
// 레거시 버스인 "기사용 NFC 탑승" 이식. 카드를 대면 서버(CF boardNfc)가 판정해
// 등록/이미탑승/미등록 을 돌려주고, 화면은 그 결과만 그린다(판정 로직 클라 0).
//
// 2026-10-20: 스토어 앱(BusLink 운행 · native/driver-android) 안에서는 네이티브 NFC(@capgo/capacitor-nfc)로
//   읽는다 — WebView 에는 NDEFReader 가 없다. 분기는 lib/passengerNfc.js startCardScan 한 곳.
// ⚠ Web NFC 는 **Android Chrome + HTTPS 전용**. iOS Safari·데스크톱엔 NDEFReader 가
//   아예 없다 → 미지원 단말은 기능을 숨기지 말고 **QR 폴백을 안내**한다(정직 안내).
// ⚠ `scan()` 은 반드시 **사용자 제스처** 안에서 호출해야 권한 프롬프트가 뜬다
//   (마운트 시 자동 호출하면 조용히 거부됨) → "태깅 시작" 버튼 필수.
function DriverNfcTag({ companyId, driver, dispatch }) {
  // "native"(BusLink 운행 앱 — 네이티브 NFC) | "web"(안드로이드 크롬 Web NFC) | "none"
  const nfcMode = detectNfcMode();
  const supported = nfcMode !== "none";
  const [scanning, setScanning] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [last, setLast] = useState(null);   // { kind:'ok'|'dup'|'unknown', name, empNo, uid }
  const [count, setCount] = useState(null); // 서버 집계 탑승 인원(오늘·이 차량)

  // 등록 모드(2026-07-22) — 기존 사원증 재사용 현장은 UID 목록이 없어 기사가 대행 등록.
  // "board"(탑승) | "register"(카드 등록) | "sleep"(빈 차 확인 — 2026-08-18 슬리핑 차일드)
  const [mode, setMode] = useState("board");
  const [sleepMsg, setSleepMsg] = useState(null); // { ok:boolean, text:string }
  const [passengers, setPassengers] = useState([]);
  const [passLoading, setPassLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null); // { empNo, name, nfcUid }
  const [pendingUid, setPendingUid] = useState(null);   // 탑승 모드에서 미등록으로 찍힌 카드
  const [regMsg, setRegMsg] = useState(null);           // { ok:boolean, text:string }

  const abortRef = useRef(null);
  const audioRef = useRef(null);
  const cooldownRef = useRef(createTagCooldown(2000));
  const busyRef = useRef(false);
  // ⚠ NDEFReader.onreading 은 scan() 호출 시점의 클로저를 붙든다 → 최신 state(mode·
  //   selectedEmp)를 못 본다. 항상 최신 핸들러를 가리키는 ref 로 우회(stale closure 방지).
  const handlerRef = useRef(() => {});

  const vehicleId = dispatch?.vehicleId || driver?.vehicleId || "";
  const routeId = dispatch?.routeId || "";

  // 언마운트 시 스캔 중단 — AbortController 없이 두면 탭을 떠나도 리더가 계속 살아있다.
  useEffect(() => {
    return () => { try { abortRef.current?.abort(); } catch (_) {} };
  }, []);

  // 등록 모드 진입 시 이 노선 승객 명단 1회 로드(사번 타이핑 대신 이름 선택).
  // where(routeId) 단일 필드 = 자동 인덱스. active 는 클라 필터(복합 인덱스 회피).
  useEffect(() => {
    if (mode !== "register" || !companyId || !routeId || passengers.length > 0) return;
    let alive = true;
    setPassLoading(true);
    getDocs(query(collection(db, "companies", companyId, "passengers"), where("routeId", "==", routeId)))
      .then(snap => {
        if (!alive) return;
        const list = snap.docs.map(d => ({ empNo: d.id, ...d.data() }))
          .filter(p => p.active !== false)
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ko"));
        setPassengers(list);
      })
      .catch(() => { if (alive) setErrMsg("승객 명단을 불러오지 못했습니다"); })
      .finally(() => { if (alive) setPassLoading(false); });
    return () => { alive = false; };
  }, [mode, companyId, routeId, passengers.length]);

  // 비프음 — 사용자 제스처(태깅 시작) 안에서 AudioContext 를 만들어야 소리가 난다
  // (swstagsys unlockAudio 선례: 제스처 밖에서 만들면 suspended 상태로 무음).
  const beep = (ok) => {
    try {
      const ctx = audioRef.current;
      if (!ctx) return;
      const play = (freq, at, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.18, ctx.currentTime + at);
        osc.start(ctx.currentTime + at);
        osc.stop(ctx.currentTime + at + dur);
      };
      if (ok) play(880, 0, 0.12);
      else { play(300, 0, 0.12); play(300, 0.16, 0.12); play(300, 0.32, 0.12); }
    } catch (_) { /* 오디오 실패는 무시 — 태깅 자체와 무관 */ }
  };

  const start = async () => {
    setErrMsg("");
    try {
      // 제스처 컨텍스트에서 오디오 언락
      if (!audioRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) audioRef.current = new AC();
      }
      if (audioRef.current?.state === "suspended") await audioRef.current.resume().catch(() => {});

      // 앱 안(네이티브 NFC)이면 폰 NFC 가 꺼져 있는지 먼저 — 꺼진 채 시작하면 아무 반응이 없어 고장으로 보인다.
      if (nfcMode === "native" && (await nativeNfcStatus()) === "NFC_DISABLED") {
        setErrMsg("휴대폰 NFC 가 꺼져 있습니다.\n휴대폰 설정 → 연결 → NFC 를 켠 뒤 다시 눌러주세요");
        return;
      }
      // 웹(Web NFC)·앱(네이티브 리더 모드) 공통 — 카드를 댈 때마다 onUid 가 온다(멈출 때까지 계속).
      // ref 경유 — 모드 전환·승객 선택이 반영된 최신 핸들러가 호출된다.
      const stopScan = await startCardScan({
        onUid: (raw) => { handlerRef.current(raw); },
        onError: () => setErrMsg("카드를 읽지 못했습니다. 다시 태그해주세요"),
      });
      abortRef.current = { abort: stopScan };
      setScanning(true);
    } catch (e) {
      setScanning(false);
      setErrMsg(
        e?.name === "NotAllowedError"
          ? "NFC 권한이 거부되었습니다.\n브라우저 설정에서 이 사이트의 NFC 를 허용해주세요"
          : e?.name === "NotSupportedError"
            ? "이 기기에서는 NFC 를 사용할 수 없습니다"
            : "NFC 시작 오류: " + (e?.message || e)
      );
    }
  };

  const stop = () => {
    try { abortRef.current?.abort(); } catch (_) {}
    abortRef.current = null;
    setScanning(false);
  };

  // 등록 모드 태깅 — 선택된 승객에게 이 카드를 매핑.
  const handleRegisterTag = async (uid) => {
    if (!selectedEmp) { setPendingUid(uid); setRegMsg(null); return; } // 승객 먼저 고르게
    try {
      const r = await registerNfcCard({ companyId, empNo: selectedEmp.empNo, uid });
      setRegMsg({ ok: true, text: `${r.name || selectedEmp.name}님 카드가 ${r.replaced ? "교체" : "등록"}되었습니다` });
      beep(true);
      if ("vibrate" in navigator) navigator.vibrate([100, 50, 100]);
      // 명단 캐시 갱신(배지 즉시 반영) + 다음 사람 등록으로 진행
      setPassengers(prev => prev.map(p => p.empNo === selectedEmp.empNo ? { ...p, nfcUid: uid } : p));
      setSelectedEmp(null); setPendingUid(null); setSearch("");
    } catch (e) {
      setRegMsg({ ok: false, text: e?.message || String(e) });
      beep(false);
    }
  };

  // ── 빈 차 확인(슬리핑 차일드) — 맨 뒷좌석 태그 (2026-08-18 건의) ──
  // 🔴 확인은 **그 차량에 등록된 태그**로만 인정한다(서버가 검사). 아무 태그나 되면
  //    사원증으로 앉은 자리에서 눌러 버리고, 그러면 이 기능은 아무것도 보장하지 않는다.
  // 태그가 아직 등록 안 됐으면 그 자리에서 등록한다(현장에서 붙이고 바로 쓰게).
  const handleSleepTag = async (uid) => {
    try {
      const r = await httpsCallable(functions, "recordSleepingCheck")({
        companyId, vehicleId, via: "nfc", nfcUid: uid,
      });
      const d = r.data || {};
      setSleepMsg({ ok: true, text: d.alreadyChecked ? "이미 확인되었습니다" : "확인되었습니다" });
      beep(true);
      if ("vibrate" in navigator) navigator.vibrate([100, 50, 100]);
      setErrMsg("");
    } catch (e) {
      const msg = e?.message || String(e);
      // 태그 미등록이면 등록 버튼을 띄운다(오류로 끝내면 기사가 할 수 있는 게 없다).
      if (/등록된 확인 태그가 없습니다/.test(msg)) {
        setSleepMsg({ ok: false, text: "이 차량에 등록된 확인 태그가 없습니다", uid });
      } else {
        setSleepMsg({ ok: false, text: msg });
      }
      beep(false);
    }
  };

  // 뒷좌석 태그를 이 차량에 등록.
  const registerSleepTag = async (uid) => {
    try {
      await httpsCallable(functions, "registerSleepTag")({ companyId, vehicleId, nfcUid: uid });
      setSleepMsg({ ok: true, text: "확인 태그를 등록했습니다. 다시 태그해주세요." });
      beep(true);
    } catch (e) {
      setSleepMsg({ ok: false, text: e?.message || String(e) });
      beep(false);
    }
  };
  const handleTag = async (serial) => {
    // 네이티브 NFC 는 바이트 배열([4,83,206,154]), Web NFC 는 "04:53:ce:9a" — 같은 hex 로.
    const uid = Array.isArray(serial) ? nfcBytesToHex(serial) : normalizeNfcUid(serial);
    if (!uid) return;
    // 카드를 대고 있으면 onreading 이 연속 발화 → 쿨다운으로 중복 호출 차단.
    if (!cooldownRef.current(uid)) return;
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      if (mode === "register") { await handleRegisterTag(uid); return; }
      if (mode === "sleep") { await handleSleepTag(uid); return; }
      const r = await boardByNfc({
        companyId, vehicleId, uid,
        selectedRouteId: dispatch?.routeId || null,
      });
      if (typeof r.todayCount === "number") setCount(r.todayCount);
      // ⚠ 미등록은 throw 가 아니라 registered:false 로 온다(boarding.js 주석 참조).
      if (!r.registered) {
        setLast({ kind: "unknown", uid: r.uid || uid });
        beep(false);
        if ("vibrate" in navigator) navigator.vibrate([200, 80, 200]);
      } else if (r.alreadyBoarded) {
        setLast({ kind: "dup", name: r.name, empNo: r.empNo });
        beep(true);
        if ("vibrate" in navigator) navigator.vibrate(60);
      } else {
        setLast({ kind: "ok", name: r.name, empNo: r.empNo });
        beep(true);
        if ("vibrate" in navigator) navigator.vibrate([100, 50, 100]);
      }
      setErrMsg("");
    } catch (e) {
      // 네트워크·배차없음 등 진짜 오류만 여기로 온다.
      setErrMsg(e?.message || String(e));
      beep(false);
    } finally {
      busyRef.current = false;
    }
  };

  // 매 렌더마다 최신 handleTag 를 ref 에 꽂는다 — scan() 이 붙든 옛 클로저 대신
  // 이 ref 를 통해 호출되므로 모드/선택 승객이 항상 최신값으로 평가된다.
  handlerRef.current = handleTag;

  // ── 미지원 단말: 숨기지 말고 이유 + 해결법 안내 ──
  // 기사 단말은 안드로이드로 통제되므로 여기 걸리는 건 대개 "크롬이 아닌 브라우저로
  // 열었다"(삼성 인터넷 등)이거나 PC 접속이다 → 원인별 해결법을 구체적으로 제시.
  if (!supported) {
    return (
      <div style={S.qrNotice}>
        <span style={{ display: "inline-flex", color: "var(--color-label-mute)" }}><Icon name="close" size={24} stroke={1.8} /></span>
        <div>
          <div style={S.qrNoticeTitle}>이 기기·브라우저에서는 NFC 태깅을 쓸 수 없습니다</div>
          <div style={S.qrNoticeSub}>
            NFC 태깅은 <b>안드로이드 + 크롬</b>에서만 동작합니다.
            <br />· 안드로이드 폰이라면 <b>크롬으로 다시 열어주세요</b>(삼성 인터넷 등 다른 브라우저는 미지원).
            <br />· 폰 <b>설정에서 NFC 가 켜져 있는지</b> 확인해주세요.
            <br />· PC·아이폰·아이패드는 지원하지 않습니다 → <b>탑승 QR</b> 탭을 사용해주세요.
          </div>
        </div>
      </div>
    );
  }

  const tone =
    last?.kind === "ok" ? { bg: "#E6F7EB", bd: "var(--color-positive)", fg: "#007A29" }
      : last?.kind === "dup" ? { bg: "var(--color-primary-soft)", bd: "var(--color-primary)", fg: "var(--color-primary-deep)" }
        : { bg: "var(--color-atomic-red-90)", bd: "var(--color-destructive)", fg: "var(--color-destructive)" };

  const filteredPass = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return passengers;
    return passengers.filter(p =>
      (p.name || "").toLowerCase().includes(q) || (p.empNo || "").toLowerCase().includes(q));
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, width: "100%" }}>
      {/* 모드 전환 — 탑승 처리 ↔ 카드 등록 */}
      <div style={{ display: "flex", gap: 6, width: "100%", maxWidth: 340 }}>
        {[["board", "탑승 처리"], ["register", "카드 등록"], ["sleep", "빈 차 확인"]].map(([m, label]) => (
          <button key={m}
            onClick={() => { setMode(m); setRegMsg(null); setSleepMsg(null); setErrMsg(""); }}
            style={{
              flex: 1, padding: "8px 0", borderRadius: 10, fontSize: 13, fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit",
              border: `1px solid ${mode === m ? "var(--color-primary)" : "var(--color-line)"}`,
              background: mode === m ? "var(--color-primary-soft)" : "var(--color-bg)",
              color: mode === m ? "var(--color-primary-deep)" : "var(--color-label-mute)",
            }}>{label}</button>
        ))}
      </div>

      {/* ─────────── 빈 차 확인 모드(슬리핑 차일드) ─────────── */}
      {mode === "sleep" ? (
        <>
          <div style={{ fontSize: 12, color: "var(--color-label-mute)", textAlign: "center", lineHeight: 1.6 }}>
            운행이 끝나면 <b>맨 뒷좌석까지 가서</b> 뒷좌석 태그를 대주세요.<br />
            차 안에 남은 사람이 없다는 기록이 남습니다.
          </div>
          {sleepMsg && (
            <div style={{
              width: "100%", maxWidth: 340, padding: "12px 14px", borderRadius: 12, textAlign: "center",
              fontSize: 14, fontWeight: 800, lineHeight: 1.5,
              background: sleepMsg.ok ? "var(--color-positive-soft, #E7F7EF)" : "var(--color-destructive-soft, #FDECEC)",
              color: sleepMsg.ok ? "var(--color-positive)" : "var(--color-destructive)",
              border: `1px solid ${sleepMsg.ok ? "var(--color-positive)" : "var(--color-destructive)"}`,
            }}>
              {sleepMsg.text}
              {sleepMsg.uid && (
                <button onClick={() => registerSleepTag(sleepMsg.uid)}
                  style={{
                    display: "block", width: "100%", marginTop: 10, padding: "10px 0", borderRadius: 10,
                    border: "none", background: "var(--color-primary)", color: "#fff",
                    fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                  }}>이 태그를 이 차량에 등록</button>
              )}
            </div>
          )}
        </>
      ) : null}
      {/* ─────────── 카드 등록 모드 ─────────── */}
      {mode === "register" ? (
        <>
          <div style={{ fontSize: 12, color: "var(--color-label-mute)", textAlign: "center", lineHeight: 1.6 }}>
            사원증 UID 목록이 없는 경우 여기서 카드를 등록합니다.<br />
            <b>승객을 고른 뒤 그 사람의 카드를 태그</b>하세요.
          </div>

          {pendingUid && !selectedEmp && (
            <div style={{
              width: "100%", maxWidth: 340, borderRadius: 12, padding: "10px 14px",
              background: "var(--color-primary-soft)", border: "1px solid var(--color-primary)", textAlign: "center",
            }}>
              <div style={{ fontSize: 12, color: "var(--color-primary-deep)", fontWeight: 700 }}>읽은 카드</div>
              <div style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 800 }}>{formatNfcUid(pendingUid)}</div>
              <div style={{ fontSize: 11, color: "var(--color-label-mute)", marginTop: 2 }}>아래에서 이 카드의 주인을 선택하세요</div>
            </div>
          )}

          {selectedEmp ? (
            <div style={{ width: "100%", maxWidth: 340, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--color-label-mute)" }}>선택된 승객</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "var(--color-label)" }}>
                {selectedEmp.name} <span style={{ fontSize: 12, color: "var(--color-label-mute)" }}>({selectedEmp.empNo})</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--color-primary-deep)", fontWeight: 700, marginTop: 10 }}>
                이제 이 승객의 사원증을 태그하세요
              </div>
              <button onClick={() => { setSelectedEmp(null); setPendingUid(null); }}
                style={{ ...S.refreshBtn, marginTop: 10 }}>다른 승객 선택</button>
            </div>
          ) : (
            <div style={{ width: "100%", maxWidth: 340 }}>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="이름 또는 사번 검색"
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 10, border: "1px solid var(--color-line)", fontSize: 14, fontFamily: "inherit", outline: "none" }} />
              <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 8, border: "1px solid var(--color-line)", borderRadius: 10 }}>
                {passLoading ? <div style={{ padding: 14, fontSize: 13, color: "var(--color-label-alt)", textAlign: "center" }}>명단 불러오는 중…</div>
                  : filteredPass.length === 0 ? <div style={{ padding: 14, fontSize: 13, color: "var(--color-label-alt)", textAlign: "center" }}>
                    {passengers.length === 0 ? "이 노선에 등록된 승객이 없습니다" : "검색 결과가 없습니다"}
                  </div>
                    : filteredPass.map(p => (
                      <button key={p.empNo}
                        onClick={() => {
                          setSelectedEmp({ empNo: p.empNo, name: p.name, nfcUid: p.nfcUid });
                          setRegMsg(null);
                          // 카드를 먼저 읽어둔 상태면 선택 즉시 등록까지 진행
                          if (pendingUid) {
                            registerNfcCard({ companyId, empNo: p.empNo, uid: pendingUid })
                              .then(r => {
                                setRegMsg({ ok: true, text: `${r.name || p.name}님 카드가 ${r.replaced ? "교체" : "등록"}되었습니다` });
                                setPassengers(prev => prev.map(x => x.empNo === p.empNo ? { ...x, nfcUid: pendingUid } : x));
                                setSelectedEmp(null); setPendingUid(null); setSearch("");
                                beep(true);
                              })
                              .catch(e => { setRegMsg({ ok: false, text: e?.message || String(e) }); beep(false); });
                          }
                        }}
                        style={{
                          display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between",
                          gap: 8, padding: "10px 12px", border: "none", borderBottom: "1px solid var(--color-line-soft)",
                          background: "var(--color-bg)", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                        }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--color-label)" }}>
                          {p.name} <span style={{ fontSize: 11, color: "var(--color-label-mute)" }}>{p.empNo}</span>
                        </span>
                        {p.nfcUid
                          ? <span style={{ fontSize: 10, fontWeight: 700, color: "#007A29", background: "#E6F7EB", padding: "2px 7px", borderRadius: 10 }}>카드 있음</span>
                          : <span style={{ fontSize: 10, color: "var(--color-label-alt)" }}>미등록</span>}
                      </button>
                    ))}
              </div>
            </div>
          )}

          {regMsg && (
            <div style={{
              width: "100%", maxWidth: 340, borderRadius: 12, padding: "12px 14px", textAlign: "center",
              background: regMsg.ok ? "#E6F7EB" : "var(--color-atomic-red-90)",
              border: `2px solid ${regMsg.ok ? "var(--color-positive)" : "var(--color-destructive)"}`,
              color: regMsg.ok ? "#007A29" : "var(--color-destructive)",
              fontSize: 13, fontWeight: 700, whiteSpace: "pre-line", lineHeight: 1.6,
            }}>{regMsg.text}</div>
          )}

          {!scanning
            ? <button onClick={start} style={{ ...S.primaryBtn, maxWidth: 280 }}>카드 읽기 시작</button>
            : <>
              <div style={{ fontSize: 12, color: "var(--color-primary-deep)", fontWeight: 700 }}>카드 대기 중…</div>
              <button onClick={stop} style={{ ...S.refreshBtn, maxWidth: 280 }}>중지</button>
            </>}
          {errMsg && (
            <div style={{ fontSize: 12, color: "var(--color-destructive)", textAlign: "center", whiteSpace: "pre-line", lineHeight: 1.6 }}>{errMsg}</div>
          )}
        </>
      ) : (
        <>
      {/* 노선 + 오늘 탑승 인원 — 레거시 버스인 태깅 화면 구성 */}
      <div style={S.qrRouteBox}>
        <div style={S.qrRouteLabel}>노선</div>
        <div style={S.qrRouteVal}>{dispatch?.routeName || "-"}</div>
      </div>
      <div style={{ textAlign: "center" }}>
        {/* 🔴 집계가 «차량» 이 아니라 «이 노선» 으로 좁혀졌다(2026-09-11 boardNfc countToday) —
            차량 단위로 두면 아침 출근분이 저녁 기사 화면에 얹힌다. 라벨을 집계와 맞춘다. */}
        <div style={{ fontSize: 12, color: "var(--color-label-mute)", marginBottom: 2 }}>오늘 이 노선 탑승</div>
        <div style={{ fontSize: 44, fontWeight: 900, lineHeight: 1.1, color: "var(--color-primary-deep)" }}>
          {count == null ? "–" : count}
        </div>
      </div>

      {!scanning ? (
        <>
          <div style={{ fontSize: 13, color: "var(--color-label-mute)", textAlign: "center", lineHeight: 1.6 }}>
            태깅을 시작하면 사원증을 대는 즉시 탑승이 기록됩니다
          </div>
          <button onClick={start} style={{ ...S.primaryBtn, maxWidth: 280 }}>사원증 태깅 시작</button>
        </>
      ) : (
        <>
          <div style={{
            width: 110, height: 110, borderRadius: "50%",
            background: "var(--color-primary-soft)", border: "2px solid var(--color-primary)",
            display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-primary)",
            animation: "blpulse 2.4s ease-out infinite",
          }}><Icon name="phone" size={44} stroke={1.5} /></div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "var(--color-primary-deep)" }}>사원증을 태그해주세요</div>
          <div style={{ fontSize: 11, color: "var(--color-label-alt)", textAlign: "center", lineHeight: 1.6 }}>
            폰 뒷면 NFC 위치에 카드를 대주세요 (기종마다 위치가 다릅니다)
          </div>
        </>
      )}

      {/* 최근 태깅 결과 */}
      {last && (
        <div style={{
          width: "100%", maxWidth: 340, borderRadius: 14, padding: "14px 16px",
          background: tone.bg, border: `2px solid ${tone.bd}`, textAlign: "center",
        }}>
          {last.kind === "unknown" ? (
            <>
              <div style={{ fontSize: 20, fontWeight: 900, color: tone.fg }}>미등록 카드</div>
              <div style={{ fontSize: 12, color: "var(--color-label-mute)", marginTop: 4 }}>
                등록되지 않은 사원증입니다 (탑승 인원에 포함되지 않음)
              </div>
              <div style={{ fontSize: 11, color: "var(--color-label-alt)", marginTop: 6, fontFamily: "monospace" }}>
                {formatNfcUid(last.uid)}
              </div>
              {/* 그 자리에서 등록으로 잇는 고리 — 카드를 다시 댈 필요 없이 UID 를 넘긴다. */}
              <button
                onClick={() => { setPendingUid(last.uid); setMode("register"); setRegMsg(null); setLast(null); }}
                style={{ ...S.primaryBtn, maxWidth: 240, marginTop: 10 }}>
                이 카드 등록하기
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 20, fontWeight: 900, color: tone.fg }}>
                {last.kind === "dup" ? "이미 탑승 처리됨" : "승차 완료"}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-label)", marginTop: 4 }}>
                {last.name || "이름 미등록"}{last.empNo ? ` (${last.empNo})` : ""}
              </div>
            </>
          )}
        </div>
      )}

      {errMsg && (
        <div style={{ fontSize: 12, color: "var(--color-destructive)", textAlign: "center", whiteSpace: "pre-line", lineHeight: 1.6 }}>
          {errMsg}
        </div>
      )}

      {scanning && <button onClick={stop} style={{ ...S.refreshBtn, maxWidth: 280 }}>태깅 중지</button>}
        </>
      )}
    </div>
  );
}
